<script>
export const MEDICATIONS_SCHEMA = {
    widgetType: 'medicationList',
    label: 'Medications',
    fields: [
        { name: 'name', type: 'string', required: true, description: 'Medication name (e.g. Lisinopril 10mg)' },
        { name: 'dosage', type: 'string', required: false, description: 'Dosage instructions' },
        { name: 'frequency', type: 'string', required: false, description: 'Frequency (e.g. 1 tablet daily)' },
        { name: 'prescriber', type: 'string', required: false, description: 'Prescribing provider' },
        {
            name: 'effectiveness',
            type: 'choice',
            required: false,
            description: 'How effective is the medication',
            options: [
                { value: 'very', label: 'Very effective' },
                { value: 'somewhat', label: 'Somewhat effective' },
                { value: 'not', label: 'Not effective' },
            ],
        },
        {
            name: 'takenAsDirected',
            type: 'choice',
            required: false,
            description: 'Is the medication taken as directed',
            options: [
                { value: 'always', label: 'Always' },
                { value: 'usually', label: 'Usually' },
                { value: 'sometimes', label: 'Sometimes' },
                { value: 'rarely', label: 'Rarely' },
                { value: 'never', label: 'Never' },
            ],
        },
    ],
};
</script>

<script setup>
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import timezoneApi from '@/common/api/timezone.api';
import { searchClinicalDrug } from '@/common/api/terminology.api';
import {
    administrationEnd,
    administrationEndAt,
    administrationTypeOf,
    buildSegment,
    carrierOf,
    DOSE_AMOUNT_OPTIONS,
    doseQuantityOf,
    hasSchedule,
    isHeld,
    isPrescribedMedication,
    isScheduleComplete,
    medicationPrescriberDisplay,
    segmentChain,
    sigTextOf,
    segmentOn,
    todayIn,
    administrationTypeColor,
    PRN_COLOR,
} from '@/common/medicationSchedule';
import { DISCONTINUE_REASON_SYSTEM, INTERVAL_UNIT_OPTIONS, MINIMUM_INTERVAL_UNIT_OPTIONS, reasonConceptOf, reasonItems, segmentEndDay } from '@/common/medicationWindow';
import * as permissions from '@/common/permissionConstants';
import { buildUserActiveMap, isInactivePractitioner } from '@/common/practitionerStatus';
import { resolveServiceTimeZone } from '@/common/serviceTimeZone';
import { doseFormOf } from '@/common/rxnormDoseForm';
import BnChip from '@/components/BnChip.vue';
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnListBox from '@/components/BnListBox.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import DataProvider from '@/components/DataProvider';
import BnMedicationSegmentDialog from '@/components/questionnaires/widgets/BnMedicationSegmentDialog.vue';
import BnMedicationTaperDialog from '@/components/questionnaires/widgets/BnMedicationTaperDialog.vue';
import { Coding, MedicationRequest, Practitioner } from '@/fhirworks';
import { useScriptSureApi } from '@/composables/api/scriptSure/useScriptSureApi';
import { useScriptSureClinicalSync } from '@/composables/api/scriptSure/useScriptSureClinicalSync';
import { useAdministrationSites } from '@/composables/useAdministrationSites';
import { useCurrentEpisode } from '@/composables/useCurrentEpisode';
import { useMedicationOrderEditor } from '@/composables/useMedicationOrderEditor';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useSharedLocationRestriction } from '@/composables/useSharedLocationRestriction';
import { useTerminologyList } from '@/composables/useTerminologyList';
import { formatDate, formatDatetime } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { required, requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { addDays, format, parseISO } from 'date-fns';
import { debounce } from 'lodash';
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useDisplay } from 'vuetify';
import { useIntervalFn } from '@vueuse/core';
import { validateChoiceFields } from '@/components/questionnaires/widgets/widgetValidation';

const vuetifyDisplay = useDisplay();

const app = getCurrentInstance();
const isPortal = app.appContext.config.globalProperties?.isPortal;

const effectivenessOptions = computed(() => [
    new Coding({
        code: 'very',
        system: 'http://code.system/is-medication-effective',
        display: 'Very effective',
    }),
    new Coding({
        code: 'somewhat',
        system: 'http://code.system/is-medication-effective',
        display: 'Somewhat effective',
    }),
    new Coding({
        code: 'not',
        system: 'http://code.system/is-medication-effective',
        display: 'Not effective',
    }),
]);

// No system: Aidbox rejects a patch that adds a Coding holding an empty string.
const takenAsDirectedOptions = computed(() => [
    new Coding({
        code: 'always',
        display: 'Always',
    }),
    new Coding({
        code: 'usually',
        display: 'Usually',
    }),
    new Coding({
        code: 'sometimes',
        display: 'Sometimes',
    }),
    new Coding({
        code: 'rarely',
        display: 'Rarely',
    }),
    new Coding({
        code: 'never',
        display: 'Never',
    }),
]);

const props = defineProps({
    title: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'compact'].includes(value),
    },
    showInactive: {
        type: Boolean,
        default: false,
    },
    scriptSurePatientId: {
        type: String,
        default: null,
    },
    patient: {
        type: Object,
        default: null,
    },
    treatmentPlanMedsOnly: {
        type: Boolean,
        default: false,
    },
    autoRefresh: {
        type: Boolean,
        default: false,
    },
});

const patientId = computed(() => props.patient?.id);

// Fall back to the patient resource so the widget works in form contexts where the prop isn't wired up
const effectiveScriptSurePatientId = computed(() => props.scriptSurePatientId || props.patient?.scriptSurePatientId || null);

const isCompact = computed(() => props.variant === 'compact');

const readOnly = computed(() => props.isLocked || props.printView);

const canViewMedications = computed(() => authStore.hasPermission([permissions.VIEW_MED_LIST, permissions.UPDATE_MED_LIST]) || isPortal);
const viewOnlyMedications = computed(() => !authStore.hasPermission(permissions.UPDATE_MED_LIST) && !isPortal && !readOnly.value);

// Null in the portal and in locked/print contexts so the lookup never fires where
// neither reconciliation nor scheduling is reachable.
const episodePatientId = computed(() => (isPortal || readOnly.value ? null : patientId.value));
const { programEncounter: currentProgramEncounter } = useCurrentEpisode(episodePatientId);

const emit = defineEmits(['update:modelValue']);
const authStore = useAuthStore();
const bnDialogRef = ref(null);
const medicationsDP = ref(null);
const medicationRequestData = ref([]);

const {
    pause: pauseRefreshTimer,
    resume: resumeRefreshTimer,
    isActive: refreshTimerActive,
} = useIntervalFn(
    () => {
        medicationsDP.value?.silentRefresh();
    },
    30000,
    { immediate: false },
);

watch(
    () => props.autoRefresh,
    (enabled) => {
        if (enabled && !refreshTimerActive.value) {
            resumeRefreshTimer();
        } else if (!enabled && refreshTimerActive.value) {
            pauseRefreshTimer();
        }
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    pauseRefreshTimer();
});

// Edit dialog state
const editDialog = ref(false);
const editingMedication = ref(null);
const originalMedication = ref(null); // Reference to original medication being edited
const isNewMedication = ref(false);

const medicationQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: [
            { query: `subject=${patientId.value}`, resourceType: 'MedicationRequest' },
            { query: `subject=${patientId.value}&code=medicationList`, resourceType: 'List' },
        ],
    };
});

const medications = computed(() => {
    return medicationRequestData.value.filter((resource) => resource.resourceType === 'MedicationRequest');
});

// Calendar days in the service's zone, not Date objects: new Date('2026-09-15') is UTC midnight,
// the previous evening anywhere west of Greenwich, so an order ending today read as already over -
// hidden from the list and locked in the dialog on its last day.
const ENDED_STATUSES = ['stopped', 'cancelled', 'completed', 'entered-in-error'];

const endDayOf = (med) => administrationEndAt(med, scheduleTimeZone.value)?.slice(0, 10) || administrationEnd(med);

const isMedicationInactive = (med) => {
    if (ENDED_STATUSES.includes(med.status)) return true;
    const endDay = endDayOf(med);
    return Boolean(endDay) && endDay < todayInServiceZone();
};

// A course that ran out on its own date stays on the list for a week, marked Completed. Dropping
// it the day after reads as the medication vanishing, and when the date was keyed wrong nobody
// notices until a dose is missed. Derived from the date and nothing else, so no job has to clear
// it: dismissing sets status completed, and the week passing simply stops this matching.
const COMPLETION_GRACE_DAYS = 7;

const isInCompletionGrace = (med) => {
    if (!med || ENDED_STATUSES.includes(med.status)) return false;
    const endDay = endDayOf(med);
    const today = todayInServiceZone();
    if (!endDay || endDay >= today) return false;
    return format(addDays(parseISO(endDay), COMPLETION_GRACE_DAYS), 'yyyy-MM-dd') >= today;
};

// The Meds card (compact) drives this through the prop from its own menu; the form widget (default)
// has no such parent, so it carries the toggle itself rather than being unable to show a past medication.
const showInactiveMedications = ref(false);
const toggleInactiveMedications = () => {
    showInactiveMedications.value = !showInactiveMedications.value;
};
const inactiveMedicationsVisible = computed(() => props.showInactive || showInactiveMedications.value);

// Which chips a row shows, decided once so the template and the chip group's gate cannot disagree.
// An alert (not scheduled, needs review) stands in for the schedule chips it would contradict.
const medicationRowChips = (med) => {
    const staff = !isPortal;
    const inactive = isMedicationInactive(med);
    const current = staff && !inactive && !hasAlertFor(med);
    const type = administrationTypeOf(med);
    const prescribed = isPrescribedMedication(med);
    return {
        inactive: staff && inactive,
        status: current && !!med.status && med.status !== 'active',
        hold: current && !!emarEnabled && !!heldSegmentOf(med),
        taper: current && type === 'taper',
        prn: current && type === 'prn',
        scheduleComplete: current && !!emarEnabled && !!isScheduleFinished(med),
        needsSchedule: staff && !!needsScheduleFor(med),
        needsReview: staff && !!needsReviewFor(med),
        selfAdmin: current && !!emarEnabled && isSelfAdministeredNow(med),
        inactiveReason: staff && inactive && !!med.statusReason?.text,
        prescribed,
        treatmentPlan: staff && !!med.includeInTreatmentPlan && !props.treatmentPlanMedsOnly,
        freeTextSync: staff && !inactive && !prescribed && !!effectiveScriptSurePatientId.value && isFreeTextMedication(med),
    };
};

const displayedMedications = computed(() => {
    let meds = medications.value.filter((med) => med.medication?.CodeableConcept?.text);

    if (!inactiveMedicationsVisible.value) {
        meds = meds.filter((med) => !isMedicationInactive(med) || isInCompletionGrace(med));
    }

    if (props.treatmentPlanMedsOnly) {
        meds = meds.filter((med) => med.includeInTreatmentPlan === true);
    }

    // Active first, then inactive — each group alphabetical by medication name (case-insensitive).
    const nameOf = (med) => (med.medication?.CodeableConcept?.text || '').toLowerCase();
    return [...meds].sort((a, b) => {
        const aInactive = isMedicationInactive(a) ? 1 : 0;
        const bInactive = isMedicationInactive(b) ? 1 : 0;
        if (aInactive !== bInactive) return aInactive - bInactive;
        return nameOf(a).localeCompare(nameOf(b));
    });
});

// Two outcomes, not four codes. stopped and cancelled are the same act - confirmDiscontinue picks
// between them on whether a dose was ever given, because the resolvers read them differently - so
// showing either word would report an implementation detail as a clinical decision. A course that
// simply ran out never gets a status at all; its elapsed end date is the only evidence.
const DISCONTINUED_STATUSES = ['stopped', 'cancelled'];

const inactiveLabel = (med) => {
    if (med?.status === 'entered-in-error') return 'Entered in error';
    return DISCONTINUED_STATUSES.includes(med?.status) ? 'Discontinued' : 'Completed';
};

// A course that ran its length is not a failure, so it does not read in the same red as an order
// somebody pulled.
const inactiveColor = (med) => (inactiveLabel(med) === 'Completed' ? 'disabled' : 'error');

// Records written before statusChanged existed still carry the moment of an immediate stop on effective.end.
const statusChangedAt = (med) => {
    if (med?.statusChanged) return med.statusChanged;
    const end = String(med?.effective?.end || '');
    return DISCONTINUED_STATUSES.includes(med?.status) && end.length > 10 ? end : null;
};

// In the facility's zone and labeled with it, so a moment reads the same from any browser.
const formatMoment = (value) => formatDatetime(value, { timezone: scheduleTimeZone.value });

const formatEndOf = (value) => (String(value || '').length > 10 ? formatMoment(value) : formatDate(value));

// Count of inactive medications (for parent component to know if menu should show). One still in its
// grace week is already on the list, so it is not something the toggle would reveal.
const inactiveMedicationsCount = computed(() => {
    return medications.value.filter((med) => med.medication?.CodeableConcept?.text && isMedicationInactive(med) && !isInCompletionGrace(med)).length;
});

watch(
    () => medicationRequestData.value,
    () => {
        if (medicationRequestData.value && !props.isLocked) {
            emit('update:modelValue', medicationRequestData.value);
        }
    },
    { immediate: true, deep: true },
);

// Open dialog to add a new medication
const openAddDialog = () => {
    medList.value = [];
    searchRxNorm.value = null;
    medNameBlurred.value = false;
    // Opens on Unknown: at intake the name is often all anyone knows, and a type chosen by default
    // saves, locks, and asks for a frequency nobody stated. The portal shows the frequency a
    // patient reports, which is what the scheduled layout lays out.
    administrationType.value = isPortal ? 'scheduled' : 'unknown';
    prnStart.value = todayInServiceZone();
    prnSelfAdministered.value = false;
    prnEnd.value = null;
    editingSegment.value = null;
    initialSegmentType.value = null;
    segmentTemplate.value = null;
    historyData.value = null;

    const newMedication = new MedicationRequest({
        subject: { id: patientId.value, resourceType: 'Patient' },
        status: 'active',
        // Not in the portal: the type is the clinician's to choose at reconciliation, and once stored it locks.
        administrationType: isPortal ? undefined : administrationType.value,
        medication: {
            CodeableConcept: {
                text: '',
            },
        },
        intent: 'proposal',
        effective: { start: todayInServiceZone() },
        dateAsserted: new Date().toISOString(),
        reported: { reference: { id: patientId.value, resourceType: 'Patient' } },
        includeInTreatmentPlan: true,
    });

    if (isPortal) {
        newMedication.status = 'draft';
    }

    editingMedication.value = newMedication;
    // The dose opens blank: under Unknown it is optional and an untouched 1 would read as a dose
    // somebody stated. Choosing a type fills it in (onAdministrationTypeChosen).
    isNewMedication.value = true;
    seedAdministrationSite();
    v$.value.medicationForm.$reset();
    baselineEdits();
    editDialog.value = true;
};

// Open dialog to edit an existing medication
const openEditDialog = (medication) => {
    // A view-only user opens the record and sees the schedule; every control is already gated
    // on viewOnlyMedications. Refusing to open it left them unable to see a schedule at all,
    // disabled or otherwise, and told them nothing about who could change it.
    if (readOnly.value) return;

    // Only a medication with no schedule can be correctable, so that is the only case worth
    // asking about - and asking is what decides whether the dose field opens editable.
    editingHasAdministrations.value = true;
    if (!hasSchedule(medication)) {
        hasAdministrations(medication).then((given) => {
            if (editingMedication.value?.id === medication.id) editingHasAdministrations.value = given;
        });
    }

    medList.value = [];
    searchRxNorm.value = null;
    medNameBlurred.value = true;
    // Store reference to original and work with a deep copy
    // Use JSON parse/stringify to properly clone FHIR resource objects
    originalMedication.value = medication;
    const medicationJson = typeof medication.toJSON === 'function' ? medication.toJSON() : JSON.parse(JSON.stringify(medication));
    editingMedication.value = new MedicationRequest(medicationJson);
    isNewMedication.value = false;
    // A patient never chooses a type, so the portal lays an Unknown medication out as it always has.
    const untyped = savedScheduleType.value === 'none' || (isPortal && savedScheduleType.value === 'unknown');
    setAdministrationType(untyped ? 'scheduled' : savedScheduleType.value);
    prnStart.value = segmentStartOf(segmentChain(editingMedication.value)[0]) || todayInServiceZone();
    prnSelfAdministered.value = segmentChain(editingMedication.value)[0]?.selfAdministered === true;
    // An unscheduled order's first window defaults to ending when the order does.
    prnEnd.value = segmentEndDay(segmentChain(editingMedication.value)[0]) || String(editingMedication.value.effective?.end || '').slice(0, 10) || null;
    editingSegment.value = null;
    initialSegmentType.value = null;
    segmentTemplate.value = null;
    v$.value.medicationForm.$reset();
    baselineEdits();
    editDialog.value = true;
    historyData.value = null;
    if (medication.id) {
        fetchMedicationHistory(medication.id);
    }
};

const RXNORM_SYSTEM = 'http://www.nlm.nih.gov/research/umls/rxnorm';

const rxNormCodesOf = (med) => (med?.medication?.CodeableConcept?.coding || []).filter((coding) => coding?.system === RXNORM_SYSTEM && coding?.code).map((coding) => String(coding.code));

const drugNameOf = (med) => (med?.medication?.CodeableConcept?.text || '').trim().toLowerCase();

/**
 * Another medication the clinic is already giving for the same drug, or null.
 *
 * Matched on RxNorm where both sides carry it and on the name otherwise, because a medication
 * with no code still schedules end to end and is exactly the kind that gets typed in twice.
 *
 * This is the only moment a duplicate can turn into a double dose - not interaction checking,
 * which is out of scope, and not a reason to refuse: two entries for one drug are sometimes
 * right, and the nurse is the one who can tell.
 */
const duplicateAdministeredMedication = (med) => {
    if (!med) return null;

    const codes = rxNormCodesOf(med);
    const name = drugNameOf(med);

    return (
        medications.value.find((other) => {
            if (!other || other.id === med.id || isMedicationInactive(other)) return false;
            if (!hasSchedule(other)) return false;

            const otherCodes = rxNormCodesOf(other);
            if (codes.length && otherCodes.length) return otherCodes.some((code) => codes.includes(code));
            return Boolean(name) && drugNameOf(other) === name;
        }) || null
    );
};

const saveMedication = async () => {
    v$.value.medicationForm.$touch();
    if (!(await v$.value.medicationForm.$validate())) return;

    // Only asked of a medication the clinic is taking on. One that is list information cannot
    // produce a dose, so a second row for the same drug is a list to tidy, not a hazard.
    const becomingAdministered = hasSchedule(editingMedication.value);
    if (becomingAdministered) {
        const duplicate = duplicateAdministeredMedication(editingMedication.value);
        if (duplicate) {
            const confirmed = await bnDialogRef.value.show({
                header: 'Already being given',
                message: `${duplicate.medication?.CodeableConcept?.text} is already on this patient's list and administered by the clinic. Scheduling this one as well means both will appear in the pass. Continue?`,
                confirm: 'Continue',
                cancel: 'Go back',
            });
            if (!confirmed) return;
        }
    }

    editingMedication.value.updatedBy = {
        id: authStore.practitioner.id,
        resourceType: authStore.practitioner.resourceType,
    };

    // The sig is composed from the order, except on a prescription where the prescriber's
    // own wording is what must be shown and synced back.
    if (!editingMedIsPrescribed.value && generatedSig.value) {
        const carrier = ensureCarrier(editingMedication.value);
        carrier.timing = mergeCarrierTiming(carrier, { code: { text: generatedSig.value } });
    } else if (!editingMedIsPrescribed.value && administrationType.value === 'unknown' && editingCarrier.value?.timing?.code) {
        // An Unknown medication whose dose was cleared would otherwise keep reading the sig it had.
        editingCarrier.value.timing = mergeCarrierTiming(editingCarrier.value, { code: undefined });
    }

    saveError.value = '';

    try {
        // A PRN order has no times to enter, so its segment is built from the order itself
        // rather than through a form. Both fields are present — the Save gate requires them.
        if (administrationType.value === 'prn' && !editingSegments.value.length && !prnOrderIncomplete.value) {
            // A PRN order hides the effective dates, so its start follows the window's rather than the dialog's default.
            editingMedication.value.effective = { ...editingMedication.value.effective, start: prnStart.value || todayInServiceZone() };
            onSegmentSaved(
                buildSegment({
                    medication: editingMedication.value,
                    type: 'prn',
                    start: prnStart.value || todayInServiceZone(),
                    ends: prnEnd.value ? { type: 'date', date: prnEnd.value } : undefined,
                    maxPerDay: editingCarrier.value.maxPerDay,
                    minimumInterval: editingCarrier.value.minimumInterval,
                    selfAdministered: prnSelfAdministered.value ? true : undefined,
                    addedBy: { id: authStore.practitioner.id, resourceType: authStore.practitioner.resourceType },
                }),
            );
        }

        await medicationsDP.value.save(editingMedication.value);
    } catch (error) {
        // The dialog stays open on a failure the user can act on, rather than closing over it.
        console.error('Failed to save medication:', error);
        saveError.value = error?.response?.data?.message || error?.message || 'Could not save this medication. Nothing was changed.';
        return;
    }

    editDialog.value = false;
    editingMedication.value = null;
    originalMedication.value = null;
    historyData.value = null;
};

const closeEdit = () => {
    editDialog.value = false;
    editingMedication.value = null;
    originalMedication.value = null;
    historyData.value = null;
};

// The scheduling forms write onto the medication being edited and are only ever persisted by
// this dialog's own Save, so a schedule, a taper or a hold entered and then cancelled is lost
// with everything else. needsSaved() compares against the JSON the resource was built from,
// which for a medication being added is the seed the dialog opened with.
/**
 * Take the dialog's opening state as the baseline for "unsaved".
 *
 * Opening derives onto the resource — the carrier, the administration type, the generated
 * sig — so without this a legacy medication is dirty the moment it is opened and every
 * cancel would stop to ask. What counts is what the USER changed after the form settled.
 */
const baselineEdits = () => {
    if (editingMedication.value) editingMedication.value.originalObjJson = editingMedication.value.toJSON();
};

const hasPendingEdits = () => Boolean(editingMedication.value?.needsSaved?.());

const cancelEdit = async () => {
    if (hasPendingEdits() && bnDialogRef.value) {
        const confirmed = await bnDialogRef.value.show({
            header: 'Discard changes',
            message: 'This medication has changes that have not been saved. Close without saving them?',
            confirm: 'Discard',
            cancel: 'Keep editing',
        });
        if (!confirmed) return;
    }
    closeEdit();
};

const scheduleTimeZone = computed(() => resolveServiceTimeZone(null));

const isMedicationSelected = computed(() => Boolean(editingMedication.value?.medication?.CodeableConcept?.text));

// Offered per program the patient is enrolled in. Left empty, a dose is given where the patient is.
const { sites: administrationSites } = useAdministrationSites(patientId);

const {
    segmentDialog,
    editingSegment,
    initialSegmentType,
    segmentTemplate,
    taperDialog,
    editingSegments,
    canEditSchedule,
    canOpenSegment,
    canEditOrderIdentity,
    canEditRoute,
    segmentStartOf,
    describeSegment,
    discontinueOrder,
    restoreOrder,
    ensureCarrier,
    editingCarrier,
    editingDose,
    editingOrderTiming,
    orderFrequency,
    orderPeriod,
    orderPeriodUnit,
    requiresDose,
    requiresRecurrence,
    requiresPrnLimits,
    prnOrderIncomplete,
    prnStartsInPast,
    effectiveStart,
    effectiveEnd,
    effectiveEndError,
    prnStartError,
    prnEndError,
    mergeCarrierTiming,
    setOrderTiming,
    prescribedSig,
    editingDoseFreeText,
    currentSegment,
    isSegmentSaved,
    editingHasAdministrations,
    isDoseLocked,
    canEditDose,
    savedScheduleType,
    scheduleSectionCy,
    isTypeLocked,
    administrationType,
    administrationTypeOptions,
    administrationTypeLabel,
    segmentSiteLabel,
    setAdministrationSite,
    seedAdministrationSite,
    prnStart,
    prnSelfAdministered,
    prnEnd,
    setOrderPrn,
    generatedSig,
    reportedAsNeeded,
    setReportedAsNeeded,
    doseUnitOptions,
    setDoseQuantity,
    setRoute,
    setAdministrationInstructions,
    openSegmentDialog,
    openTaperDialog,
    setAdministrationType,
    onAdministrationTypeChosen,
    chooseAdministrationType,
    heldSegment,
    canHold,
    releaseHold,
    onSegmentSaved,
    onTaperSaved,
    onSegmentDeleted,
} = useMedicationOrderEditor({
    editingMedication,
    originalMedication,
    isNewMedication,
    // A getter because editingMedIsPrescribed is declared further down.
    editingMedIsPrescribed: () => editingMedIsPrescribed.value,
    readOnly,
    viewOnlyMedications,
    isMedicationSelected,
    scheduleTimeZone,
    administrationSites,
    editDialog,
    endDayOf,
    confirm: (options) => bnDialogRef.value?.show(options),
    isPortal: Boolean(isPortal),
});

// Where the eMAR has not been released, nothing reads a schedule, a taper, a site or the nurse's
// instructions, so the dialog does not ask for them. Unknown, Scheduled and PRN still say how it is taken.
const emarEnabled = useFeatureFlags().isEnabled('emar');
const offeredAdministrationTypes = emarEnabled ? administrationTypeOptions : administrationTypeOptions.filter((option) => option.value !== 'taper');
const administrationScheduleInfo =
    "A medication's schedule is what adds it to the eMAR. Its doses come due on the medication pass from the schedule's start date. " +
    'If the patient takes this medication themselves, check <b>Self-doses</b> on the schedule: it stays visible in the eMAR, but no dose is ever due.';

const todayInServiceZone = () => todayIn(scheduleTimeZone.value);

// Read off the chain rather than off MedicationRequest.status: a hold that ends on a date
// releases itself, and a stored status would have to be rewritten by something on that day.
const heldSegmentOf = (med) => {
    const segment = segmentOn(med, todayInServiceZone(), scheduleTimeZone.value);
    return isHeld(segment) ? segment : null;
};

// The chain has run out: every segment written has finished and none replaced it. Nothing
// auto-discontinues on this and nothing prompts — the row says so, and whether to
// discontinue stays the clinician's decision.
const isScheduleFinished = (med) => isScheduleComplete(med, todayInServiceZone(), scheduleTimeZone.value);

// Read off the window in force: every segment carries its own answer, so the row follows a
// stretch of home leave in and out of it.
const isSelfAdministeredNow = (med) => segmentOn(med, todayInServiceZone(), scheduleTimeZone.value)?.selfAdministered === true;

// Offered, not enforced: each is a combobox, so an uncommon value is still typeable.
const countUpTo = (last, first = 1) => Array.from({ length: last - first + 1 }, (item, index) => String(first + index));

const doseAmountOptions = DOSE_AMOUNT_OPTIONS;
const orderFrequencyOptions = countUpTo(10);
const orderIntervalOptions = countUpTo(30);
const prnMaxPerDayOptions = countUpTo(24);
const prnMinimumIntervalOptions = countUpTo(24);
const routeOptions = ['Oral', 'Sublingual', 'Buccal', 'Topical', 'Transdermal', 'Subcutaneous', 'Intramuscular', 'Intravenous', 'Rectal', 'Vaginal', 'Nasal', 'Inhalation', 'Ophthalmic', 'Otic'];

const medicationForm = computed(() => ({
    medicationName: editingMedication.value?.medication?.CodeableConcept?.text || '',
    dose: editingDose.value?.value ?? '',
    frequency: editingOrderTiming.value?.frequency ?? '',
    interval: editingOrderTiming.value?.period ?? '',
    maxPerDay: editingCarrier.value?.maxPerDay ?? '',
    minimumInterval: editingCarrier.value?.minimumInterval?.value ?? '',
}));
const medicationValidations = {
    medicationForm: {
        medicationName: { required },
        dose: { required: requiredIf(requiresDose) },
        frequency: { required: requiredIf(requiresRecurrence) },
        interval: { required: requiredIf(requiresRecurrence) },
        maxPerDay: { required: requiredIf(requiresPrnLimits) },
        minimumInterval: { required: requiredIf(requiresPrnLimits) },
    },
};
const v$ = useVuelidate(medicationValidations, { medicationForm });

// The order's own dates, separate from any schedule's. Both bound the doses the resolver expects,
// whatever a window says - which is why they stay editable after scheduling: dates are where the
// keying mistakes are, and a misdated order has to be correctable without re-entering it.
// Open through the grace week too: that week exists so a wrong end date can be moved forward.
const canEditEffectiveDates = computed(
    () => !viewOnlyMedications.value && !readOnly.value && isMedicationSelected.value && (!isEditingMedicationInactive.value || isInCompletionGrace(originalMedication.value)),
);

const canSaveMedication = computed(
    () =>
        !viewOnlyMedications.value &&
        !v$.value.medicationForm.$invalid &&
        !prnOrderIncomplete.value &&
        !prnStartsInPast.value &&
        !prnStartError.value &&
        !prnEndError.value &&
        !effectiveEndError.value,
);

const saveError = ref('');

// Reconcile mode state
const { syncMedicationToScriptSure, ensureScriptSurePatient, resolveAndSyncResource, hasScriptSureCredentials, consumeLastRegistrationUrl, buildSignInDataForPatient, loadActingOnBehalfPrescriberId } =
    useScriptSureClinicalSync();
const { signIn } = useScriptSureApi();

// Snackbar notification for ScriptSure missing patient data
const scriptSureSnackbar = ref(false);
const scriptSureSnackbarMessage = ref('');
const reconcileMode = ref(false);
const selectedForReconcile = ref(new Set());
const reconciling = ref(false);

const canReconcile = computed(() => {
    return authStore.hasPermission(permissions.UPDATE_MED_LIST) && !readOnly.value && displayedMedications.value.length > 0;
});

// e-Prescribe gating: ScriptSure enabled, prescriber registered, not the patient portal, has medication permission.
// hasScriptSureCredentials already covers account.scriptSureEnabled + practiceId + practitioner.scriptSurePrescriberId.
const canPrescribe = computed(() => {
    return hasScriptSureCredentials.value && !isPortal && authStore.hasPermission(permissions.UPDATE_MED_LIST) && !readOnly.value;
});

const isPrescribing = ref(false);
const isRefreshing = ref(false);

const openPrescribe = async () => {
    if (isPrescribing.value) return;
    isPrescribing.value = true;
    try {
        const result = await ensureScriptSurePatient(props.patient);

        if (result?.missingFields) {
            scriptSureSnackbarMessage.value = `e-Prescribe unavailable: missing ${result.missingFields.join(', ')}`;
            scriptSureSnackbar.value = true;
            return;
        }
        if (!result) {
            scriptSureSnackbarMessage.value = 'e-Prescribe is not configured or credentials are missing';
            scriptSureSnackbar.value = true;
            return;
        }

        // ensureScriptSurePatient may have just registered the patient and cached the session URL;
        // consume it to avoid a redundant signIn round-trip.
        let scriptSureUrl = consumeLastRegistrationUrl();

        if (!scriptSureUrl) {
            const signInData = buildSignInDataForPatient(props.patient);
            signInData.scriptSurePatientId = result;
            const response = await signIn(signInData);
            scriptSureUrl = response?.scriptSureUrl;
        }

        if (scriptSureUrl) {
            const width = 1200;
            const height = 800;
            const left = window.screenX + 50;
            const top = window.screenY + 50;
            window.open(scriptSureUrl, 'ScriptSure', `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`);
        } else {
            scriptSureSnackbarMessage.value = 'Failed to open e-Prescribe';
            scriptSureSnackbar.value = true;
        }
    } catch (err) {
        console.error('Failed to open e-Prescribe:', err);
        scriptSureSnackbarMessage.value = err?.response?.data?.message || err?.message || 'Failed to open e-Prescribe';
        scriptSureSnackbar.value = true;
    } finally {
        isPrescribing.value = false;
    }
};

const refreshMedications = async () => {
    if (isRefreshing.value) return;
    isRefreshing.value = true;
    try {
        await medicationsDP.value?.refresh();
    } finally {
        isRefreshing.value = false;
    }
};

const selectedCount = computed(() => selectedForReconcile.value.size);

const toggleReconcileMode = () => {
    if (reconcileMode.value) {
        // Exiting reconcile mode - clear selections
        reconcileMode.value = false;
        selectedForReconcile.value = new Set();
    } else {
        reconcileMode.value = true;
    }
};

const toggleMedicationSelection = (medId) => {
    const newSet = new Set(selectedForReconcile.value);
    if (newSet.has(medId)) {
        newSet.delete(medId);
    } else {
        newSet.add(medId);
    }
    selectedForReconcile.value = newSet;
};

const allSelectedForReconcile = computed(() => {
    const medsWithIds = displayedMedications.value.filter((med) => med.id);
    return medsWithIds.length > 0 && selectedForReconcile.value.size === medsWithIds.length;
});

const toggleAllForReconcile = () => {
    if (allSelectedForReconcile.value) {
        selectedForReconcile.value = new Set();
    } else {
        const newSet = new Set();
        displayedMedications.value.forEach((med) => {
            if (med.id) newSet.add(med.id);
        });
        selectedForReconcile.value = newSet;
    }
};

/**
 * Get the last reconciliation entry from a medication's reconciliation array.
 */
const getLastReconciliation = (med) => {
    if (!med.reconciliation?.length) return null;
    return med.reconciliation[med.reconciliation.length - 1];
};

const reconcileMedications = async () => {
    if (selectedForReconcile.value.size === 0) return;

    reconciling.value = true;
    try {
        const now = new Date().toISOString();
        const practitionerId = authStore.practitioner?.id;

        // Find the active PROGRAM encounter for this patient
        const programEncounterRef = currentProgramEncounter.value?.id ? { id: currentProgramEncounter.value.id, resourceType: 'Encounter' } : null;

        // Resolve scriptSurePatientId: use prop, or auto-initialize from patient prop
        const scriptSurePatientId = props.scriptSurePatientId || (props.patient ? await ensureScriptSurePatient(props.patient) : null);

        // If ensureScriptSurePatient returned missing fields, notify the user once
        if (scriptSurePatientId && typeof scriptSurePatientId === 'object' && scriptSurePatientId.missingFields) {
            scriptSureSnackbarMessage.value = `e-Prescribe sync skipped: missing ${scriptSurePatientId.missingFields.join(', ')}`;
            scriptSureSnackbar.value = true;
        }

        // Only sync if we got a valid string ID
        const canSync = typeof scriptSurePatientId === 'string' && scriptSurePatientId;

        // Update each selected medication with reconciliation data
        for (const med of displayedMedications.value) {
            if (!selectedForReconcile.value.has(med.id)) continue;

            // Build the reconciliation entry
            const reconciliationEntry = { date: now };
            if (practitionerId) {
                reconciliationEntry.practitioner = { id: practitionerId, resourceType: 'Practitioner' };
            }
            if (programEncounterRef) {
                reconciliationEntry.encounter = programEncounterRef;
            }

            // Push to the reconciliation array
            if (!med.reconciliation) {
                med.reconciliation = [];
            }
            med.reconciliation.push(reconciliationEntry);

            if (med.status === 'draft') {
                med.status = 'active';
                med.statusChanged = new Date().toISOString();
            }

            await medicationsDP.value.save(med);

            // Push to ScriptSure if patient has a connection — skip free-text meds without RxNorm coding
            if (canSync && !isFreeTextMedication(med)) {
                try {
                    const syncResult = await syncMedicationToScriptSure(med, scriptSurePatientId);
                    if (syncResult?.drugNotFound) {
                        // FDB lookup missed (commonly a retired RxNorm code) — prescription still posts with raw codes, so don't alarm the user
                        console.warn('ScriptSure FDB miss for', med.medication?.CodeableConcept?.text);
                    }
                } catch (err) {
                    console.error('Failed to sync medication to ScriptSure:', err);
                }
            }
        }

        // Exit reconcile mode
        reconcileMode.value = false;
        selectedForReconcile.value = new Set();
    } finally {
        reconciling.value = false;
    }
};

// Modifies editingMedication (not originalMedication) so the change respects the dialog's Save/Cancel flow.
const toggleTreatmentPlanFromDialog = () => {
    if (!editingMedication.value) return;
    editingMedication.value.includeInTreatmentPlan = !editingMedication.value.includeInTreatmentPlan;
};

const addItem = async (data) => {
    const invalidChoices = validateChoiceFields(data, MEDICATIONS_SCHEMA);
    if (invalidChoices) return invalidChoices;

    const med = new MedicationRequest({
        subject: { id: patientId.value, resourceType: 'Patient' },
        status: 'active',
        intent: 'proposal',
        medication: { CodeableConcept: { text: data.name || '' } },
        dateAsserted: new Date().toISOString(),
        reported: { reference: { id: patientId.value, resourceType: 'Patient' } },
        includeInTreatmentPlan: true,
    });
    if (data.dosage) ensureCarrier(med).text = data.dosage;
    if (data.frequency) updateFrequency(med, data.frequency);
    if (data.prescriber) med.requester = { display: data.prescriber };
    if (data.effectiveness) {
        const match = effectivenessOptions.value.find((o) => o.code === data.effectiveness);
        if (match) med.isMedicationEffective = match;
    }
    if (data.takenAsDirected) {
        const match = takenAsDirectedOptions.value.find((o) => o.code === data.takenAsDirected);
        if (match) med.takenAsDirected = match;
    }
    await medicationsDP.value.save(med);

    const nilKnownList = medicationRequestData.value.find((r) => r.resourceType === 'List' && r.emptyReason?.coding?.[0]?.code === 'nilknown');
    if (nilKnownList) {
        await medicationsDP.value.deleteItem(nilKnownList);
    }

    return { success: true };
};

const getItems = () => {
    return medications.value
        .filter((med) => med.medication?.CodeableConcept?.text)
        .map((med) => ({
            name: med.medication?.CodeableConcept?.text || '',
            dosage: med.dosageInstruction?.[0]?.text || '',
            frequency: med.dosageInstruction?.[0]?.timing?.code?.text || '',
            prescriber: med.requester?.display || '',
            effectiveness: med.isMedicationEffective?.code || '',
            takenAsDirected: med.takenAsDirected?.code || '',
            status: med.status || '',
        }));
};

defineExpose({
    openAddDialog,
    openEditDialog,
    inactiveMedicationsCount,
    reconcileMode,
    toggleReconcileMode,
    canPrescribe,
    openPrescribe,
    isPrescribing,
    refreshMedications,
    isRefreshing,
    v$,
    addItem,
    getItems,
});

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('medicationList', { addItem, getItems, schema: MEDICATIONS_SCHEMA });
    // Ensure acting-on-behalf prescriber ID is loaded so canPrescribe is accurate.
    loadActingOnBehalfPrescriberId?.();
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('medicationList');
});

const isEditingMedicationInactive = computed(() => {
    return editingMedication.value ? isMedicationInactive(editingMedication.value) : false;
});

const persistStatusChange = async (medicationToUpdate, operationLabel) => {
    medicationToUpdate.statusChanged = new Date().toISOString();
    medicationToUpdate.updatedBy = {
        id: authStore.practitioner.id,
        resourceType: authStore.practitioner.resourceType,
    };
    await medicationsDP.value.save(medicationToUpdate);
    closeEdit();

    if (!medicationToUpdate.scriptSurePrescriptionId) return;
    // The change is saved by now, so a failure here must not report that nothing changed.
    try {
        await resolveAndSyncResource({
            resource: medicationToUpdate,
            patient: props.patient,
            scriptSurePatientId: props.scriptSurePatientId,
            syncFn: syncMedicationToScriptSure,
            operationLabel,
        });
    } catch (error) {
        $log.error(`MedicationsListWidget: ${operationLabel} saved but did not reach e-Prescribe`, medicationToUpdate.id, error?.message);
        scriptSureSnackbarMessage.value = 'The medication was saved, but e-Prescribe could not be updated.';
        scriptSureSnackbar.value = true;
    }
};

// A status change is written on a copy. The list shows the original, so a save that fails leaves
// the list as it was; one that succeeds replaces it.
const copyForStatusChange = (med) => {
    const copy = new MedicationRequest(typeof med.toJSON === 'function' ? med.toJSON() : JSON.parse(JSON.stringify(med)));
    if (med.originalObjJson) copy.originalObjJson = JSON.parse(JSON.stringify(med.originalObjJson));
    return copy;
};

const changeStatus = async (medication, apply, operationLabel) => {
    const updated = copyForStatusChange(originalMedication.value || medication);
    apply(updated);
    saveError.value = '';
    try {
        await persistStatusChange(updated, operationLabel);
    } catch (error) {
        $log.error(`MedicationsListWidget: ${operationLabel} failed`, updated.id, error?.message);
        saveError.value = error?.response?.data?.message || error?.message || 'Could not change this medication. Nothing was changed.';
    }
};

// Discontinue dialog state
const discontinueDialog = ref(false);
const discontinueError = ref('');
const discontinuing = ref(false);
const discontinueDate = ref(null);
const discontinueReason = ref('');
const discontinueTarget = ref(null);
const { items: discontinueReasonConcepts } = useTerminologyList({ systems: [DISCONTINUE_REASON_SYSTEM] }, { sort: 'code' });

const resourcesOf = (response) => (Array.isArray(response?.data) ? response.data : []);

/**
 * Has this medication ever been administered, given or not?
 *
 * A failed lookup answers true: the answer locks the dose, and unlocking it wrongly lets a dose be
 * edited under records that were written against it.
 */
const hasAdministrations = async (medication) => {
    if (!medication?.id) return false;
    try {
        const response = await $httpFhirApi.get(`MedicationAdministration?request=${encodeURIComponent(medication.id)}&_count=1&_elements=id`);
        return resourcesOf(response).length > 0;
    } catch (error) {
        console.error('Could not count administrations', medication.id, error);
        return true;
    }
};

/**
 * Was anything ever owed on this medication before now?
 *
 * cancelled voids every day, so answering no wrongly erases doses that came due and were never
 * given. A failed lookup answers yes.
 */
const hasComeDue = async (medication) => {
    if (!medication?.id) return false;
    try {
        const [administered, doses] = await Promise.all([
            hasAdministrations(medication),
            // A withdrawn dose was never owed, and a dose is materialized ahead of its slot.
            $httpFhirApi.get(`BN_MedPassDose?request=${encodeURIComponent(medication.id)}&doseStatus=open,closed&_sort=slot&_count=1&_elements=slot`),
        ]);
        if (administered) return true;
        const earliestSlot = String(resourcesOf(doses)[0]?.slot || '').slice(0, 16);
        return Boolean(earliestSlot) && earliestSlot <= timezoneApi.getLocalDateTime(new Date().toISOString(), "yyyy-MM-dd'T'HH:mm", scheduleTimeZone.value);
    } catch (error) {
        console.error('Could not tell whether a dose came due before discontinuing', medication.id, error);
        return true;
    }
};

const discontinueReasonItems = computed(() => reasonItems(discontinueReasonConcepts.value));

// Backdating a stop date rewrites what was expected on days that have already been reported,
// turning doses that were genuinely due into doses nobody owed. The chain records history by
// accumulating, so there is nothing a past end date expresses that a past segment does not.
const discontinueStartsInPast = computed(() => Boolean(discontinueDate.value) && discontinueDate.value < todayInServiceZone());

const canConfirmDiscontinue = computed(() => Boolean(discontinueDate.value) && !discontinueStartsInPast.value);

// A medication that reached Fresh through e-Prescribe but carries no scriptSurePrescriptionId
// came in over NCPDP, and nothing on that channel sends a cancellation back out. Discontinuing
// it here is silent as far as the pharmacy is concerned.
const discontinueNeedsPharmacyNotice = computed(() => {
    const med = discontinueTarget.value;
    return Boolean(med && isPrescribedMedication(med) && !med.scriptSurePrescriptionId);
});

const inactivateMedication = (medication) => {
    discontinueTarget.value = originalMedication.value || medication;
    // The facility's day, not UTC's. confirmDiscontinue writes the stopping instant only when
    // this equals today there, so a UTC date taken after 17:00 Mountain reads as tomorrow, the
    // comparison misses, and the order stops at a bare future day - leaving tonight's doses
    // expected and reporting them as missed.
    discontinueDate.value = todayInServiceZone();
    discontinueReason.value = '';
    discontinueError.value = '';
    discontinueDialog.value = true;
};

const cancelDiscontinue = () => {
    discontinueDialog.value = false;
    discontinueTarget.value = null;
    discontinueDate.value = null;
    discontinueReason.value = '';
};

const confirmDiscontinue = async () => {
    if (!discontinueTarget.value || discontinuing.value) return;

    discontinuing.value = true;
    discontinueError.value = '';

    const medicationToInactivate = copyForStatusChange(discontinueTarget.value);
    const stopsNow = discontinueDate.value === todayInServiceZone();
    // Today stops at this moment, so the doses still ahead of it are not expected. A future
    // date states only the day, which is the whole of it - nothing is being cut short there.
    const endsAt = stopsNow ? new Date().toISOString() : discontinueDate.value;
    // cancelled only when nothing was ever owed: the resolvers void every day of a cancelled order.
    // A future stop still owes the doses between now and then.
    const status = !stopsNow || (await hasComeDue(medicationToInactivate)) ? 'stopped' : 'cancelled';
    discontinueOrder(medicationToInactivate, { endDate: discontinueDate.value, endsAt, status });

    // A reason typed rather than chosen is still a reason - stored as text with no code
    // invented for it, which is also what keeps this working before the migration lands.
    const chosenReason = (discontinueReason.value || '').trim();
    medicationToInactivate.statusReason = reasonConceptOf(discontinueReasonConcepts.value, chosenReason, DISCONTINUE_REASON_SYSTEM);

    try {
        await persistStatusChange(medicationToInactivate, 'medication inactivation');
    } catch (error) {
        $log.error('MedicationsListWidget: could not discontinue the medication', error?.message, error?.stack);
        discontinueError.value = error?.response?.data?.message || error?.message || 'Could not discontinue this medication. Nothing was changed.';
        return;
    } finally {
        discontinuing.value = false;
    }

    discontinueDialog.value = false;
    discontinueTarget.value = null;
    discontinueDate.value = null;
    discontinueReason.value = '';
};

// Dismisses a course that ran out on its own date. completed is the FHIR state for exactly that, and
// every reader of inactive already honors it, so the medication leaves the list with no new field;
// updatedBy and history say who and when, and Restore undoes it.
const markCompleted = (medication) => {
    const options = {
        header: 'Mark completed',
        message: 'This medication has run its course. Remove it from the active list?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        await changeStatus(medication, (med) => (med.status = 'completed'), 'medication completed');
    });
};

const markEnteredInError = (medication) => {
    const options = {
        header: 'Entered in error',
        message: 'Are you sure you want to mark this medication as entered in error?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        await changeStatus(medication, (med) => (med.status = 'entered-in-error'), 'medication entered-in-error');
    });
};

const restoreMedication = (medication) => {
    const options = {
        header: 'Restore',
        message: 'Are you sure you want to restore this medication to active?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        await changeStatus(medication, (med) => restoreOrder(med, DISCONTINUED_STATUSES.includes(med.status)), 'medication reactivation');
    });
};

// Merges into the carrier. Replacing dosageInstruction wholesale was harmless while the
// array held only free text; it now destroys the dose, route, instructions and every
// schedule segment.
const updateFrequency = (med, frequency) => {
    ensureCarrier(med).timing = { code: { text: frequency } };
};

const updateEffective = (med, prop, value) => {
    let existingProp = prop === 'start' ? 'end' : 'start';
    if (!med.effective) med.effective = {};

    if (!value) {
        // Allow clearing a date (e.g. removing end date to reactivate)
        med.effective = { [existingProp.toString()]: med.effective[existingProp.toString()] };
        if (prop === 'end') {
            // Clearing the end date hides the Inactive reason field; clear the underlying value too.
            med.statusReason = undefined;
        }
        return;
    }

    if (!med.effective?.[prop.toString()]) {
        med.effective = { [existingProp.toString()]: med.effective[existingProp.toString()], [prop.toString()]: value };
    }

    med.effective[prop.toString()] = value;
};

const medList = ref([]);

const searchRxNorm = ref(null);
const notificationStore = useNotificationStore();
const debouncedSearch = debounce(
    function (val) {
        if (!val) return;
        searchClinicalDrug(val)
            .then((sub) => {
                medList.value = sub;
            })
            .catch((error) => {
                console.error('RxNorm clinical drug search failed', { search: val, error });
                notificationStore.add({ message: 'Failed to search medications. Please try again.', color: 'error' });
            });
    },
    500,
    { maxWait: 750 },
);
watch(() => searchRxNorm.value, debouncedSearch);

const setMedication = function (med, item) {
    if (typeof item === 'string' && item) {
        med.medication.CodeableConcept.text = item;
        med.medication.CodeableConcept.coding = [];
    }

    if (!searchRxNorm.value || !item) return;
    if (item.code) {
        med.medication.CodeableConcept = {
            text: item.display,
            coding: [
                {
                    code: item.code,
                    system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                    display: item.display,
                },
            ],
        };
        applyDoseForm(item.display);
    }
};

// The route and the unit describe the drug, so choosing a different one re-derives both —
// including to blank, when the new form names neither. Nothing here is worth preserving over
// what the drug itself says: leaving Oral on an injectable is the failure to avoid, and every
// field locks on save anyway.
const applyDoseForm = (display) => {
    const { route, unit } = doseFormOf(display);
    setRoute(route || '');
    setDoseQuantity({ unit: unit || '' });
};

const isFreeTextMedication = (med) => {
    const coding = med.medication?.CodeableConcept?.coding;
    return !coding || coding.length === 0 || !coding.some((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm' && c.code);
};

const editingMedIsFreeText = computed(() => {
    return editingMedication.value ? isFreeTextMedication(editingMedication.value) : false;
});

const editingMedIsPrescribed = computed(() => {
    return editingMedication.value ? isPrescribedMedication(editingMedication.value) : false;
});

// One answer for the list row and the dialog, so the two chips cannot drift apart. A prescription
// with no status yet has been sent and not answered, which is the same unfinished state as an
// error as far as the chip's color goes.
const ePrescribeChipFor = (med) => {
    const accepted = med?.ncpdpStatus === 'Accepted';
    return {
        accepted,
        color: accepted ? 'success' : 'warning',
        icon: accepted ? 'check' : 'exclamation-triangle',
        label: getNcpdpDisplay(med?.ncpdpStatus)?.label || 'Awaiting pharmacy response',
        // Newest first: the hover answers "where is this now" before "how did it get here".
        history: [...(med?.scriptSureWebhookLog || [])].reverse(),
    };
};

const ePrescribeChip = computed(() => ePrescribeChipFor(editingMedication.value));

// An as-needed order is given on request, so it needs no schedule to be administerable. An Unknown
// one has no order to schedule yet; what it needs is Needs review.
// Reads editingSegments rather than hasSchedule() so a segment added in this dialog clears it
// before the save.
const needsSchedule = computed(() => {
    if (!emarEnabled) return false;
    if (!editingMedication.value || isNewMedication.value || administrationType.value === 'prn' || administrationType.value === 'unknown') return false;
    return !isMedicationInactive(editingMedication.value) && editingSegments.value.length === 0;
});

// A prescription states a range - "1 to 2 tablets every 4 to 6 hours" - and BN holds one dose
// and one interval, so the order arrives with the fields a person has to choose left blank.
// Nothing else on the row distinguishes that from an order that simply has not been scheduled.
// An as-needed order states limits where a scheduled one states a recurrence, so asking both for
// a frequency marks every complete PRN order as incomplete. Same split the Save gate makes.
const orderIsIncomplete = (carrier, isPrn, dose, frequency) => {
    if (!dose) return true;
    return isPrn ? !(carrier?.maxPerDay && carrier?.minimumInterval?.value) : !frequency;
};

const needsScheduleFor = (med) => emarEnabled && !isMedicationInactive(med) && administrationTypeOf(med) !== 'prn' && administrationTypeOf(med) !== 'unknown' && !hasSchedule(med);

// An alert says what to do next; a type or status chip describes what the medication already is.
// Shown together they compete, so while there is something to do the description steps aside.
const hasAlertFor = (med) => needsScheduleFor(med) || needsReviewFor(med);

// A medication recorded as Unknown has an order nobody has written yet, prescribed or not.
const needsReviewFor = (med) => {
    if (!emarEnabled || !med || isMedicationInactive(med)) return false;
    if (administrationTypeOf(med) === 'unknown') return true;
    if (!isPrescribedMedication(med)) return false;
    const carrier = carrierOf(med);
    return orderIsIncomplete(carrier, administrationTypeOf(med) === 'prn', doseQuantityOf(carrier)?.value, carrier?.timing?.repeat?.frequency);
};

// Not on a medication being added, like Not scheduled: it reports the saved record's state.
const needsReview = computed(() => {
    if (!emarEnabled || !editingMedication.value || isMedicationInactive(editingMedication.value)) return false;
    if (administrationType.value === 'unknown') return !isNewMedication.value;
    if (!editingMedIsPrescribed.value) return false;
    return orderIsIncomplete(editingCarrier.value, administrationType.value === 'prn', editingDose.value?.value, orderFrequency.value);
});

const needsReviewHint = computed(() =>
    administrationType.value === 'unknown'
        ? 'How this medication is taken has not been confirmed. Choose how it is administered and fill in the order.'
        : 'The prescription states a range. Choose the dose and how often it is given.',
);

const medNameBlurred = ref(false);
const onMedNameBlur = () => {
    medNameBlurred.value = true;
};
// A prescription that came from e-Prescribe cannot fail to sync to it. Some drugs - a multivitamin -
// carry an NDC and no RxNorm concept, which is what this check keys on.
const showFreeTextWarning = computed(() => {
    return (
        !isPortal &&
        !editingMedIsPrescribed.value &&
        effectiveScriptSurePatientId.value &&
        medNameBlurred.value &&
        editingMedication.value?.medication?.CodeableConcept?.text &&
        editingMedIsFreeText.value
    );
});

const editingPrescriberDisplay = computed(() => (editingMedication.value ? medicationPrescriberDisplay(editingMedication.value) : ''));

const { scopeUserUrl } = useSharedLocationRestriction();
const prescriberOptions = ref([]);
let prescriberOptionsRequested = false;

// Fetched on first touch rather than on mount: this component renders in the Meds card and in every
// form widget, and most of those never open the prescriber field. A failure retries next touch.
const ensurePrescriberOptions = async () => {
    if (prescriberOptionsRequested) return;
    prescriberOptionsRequested = true;
    try {
        // In-app deactivation flips User.active, not Practitioner.active, so the linked Users are
        // what separates a current provider from a retired one.
        const response = await $httpFhirApi.get(scopeUserUrl('User?practitioner:missing=false&_include=practitioner'));
        const resources = Array.isArray(response.data) ? response.data : [];
        const userActiveById = buildUserActiveMap(resources);
        // fullName is a fhirworks getter, so the raw JSON the API returns has none — hydrate before
        // filtering on it or every provider drops out.
        prescriberOptions.value = resources
            .filter((res) => res.resourceType === 'Practitioner' && !isInactivePractitioner(res, userActiveById))
            .map((res) => new Practitioner(res))
            .filter((practitioner) => practitioner.fullName);
    } catch (err) {
        console.error('Failed to load prescriber options:', err);
        prescriberOptions.value = [];
        prescriberOptionsRequested = false;
    }
};

const prescriberSearch = ref('');
const prescriberMenu = ref(false);
const prescriberFocused = ref(false);

// Whitespace is tidied when the field is left rather than while typing: a display trimmed mid-word
// feeds model-value, the combobox resyncs its text from it, and the space just typed disappears.
const trimOrClearPrescriber = (med) => {
    const display = med?.requester?.display;
    if (typeof display !== 'string') return;
    const trimmed = display.trim();
    if (!trimmed) {
        med.requester = undefined;
        return;
    }
    if (trimmed === display) return;
    // Named fields, not a spread: a fhirworks Reference's accessors don't enumerate, so spreading it
    // would silently drop a picked provider's id.
    const { id, resourceType } = med.requester;
    med.requester = id ? { id, resourceType, display: trimmed } : { display: trimmed };
};

const onPrescriberFocused = (isFocused) => {
    prescriberFocused.value = isFocused;
    if (isFocused) ensurePrescriberOptions();
    else trimOrClearPrescriber(editingMedication.value);
};

// The search term is only read, for filtering — never bound back into the combobox.
const onPrescriberSearch = (term) => {
    prescriberSearch.value = term ?? '';
    ensurePrescriberOptions();
};

// Filtering happens here rather than in the combobox so the menu can stay shut when the typed name
// matches nobody — left to itself Vuetify pops an empty list on every keystroke.
const prescriberMatches = computed(() => {
    const term = prescriberSearch.value?.trim().toLowerCase();
    if (!term) return prescriberOptions.value;
    return prescriberOptions.value.filter((practitioner) => practitioner.fullName?.toLowerCase().includes(term));
});

// After a pick the search reads back the chosen provider's name — the field displaying its selection,
// not someone searching. Only a pick carries an id: typed text is written into the value on every
// keystroke, so it always equals the search and must not close the menu. Matches also change after
// blur (the directory resolving late, the blur trim resyncing the search), so only a focused field opens.
watch(prescriberMatches, (matches) => {
    const showingPick = !!editingMedication.value?.requester?.id && prescriberSearch.value === editingPrescriberDisplay.value;
    prescriberMenu.value = prescriberFocused.value && matches.length > 0 && !showingPick;
});

// Only a picked provider carries an id — text that merely matches a provider's name stays free
// text. Aidbox rejects null, so clearing the field removes the reference entirely.
const setPrescriber = (value, med) => {
    if (!med) return;
    if (typeof value === 'string') {
        med.requester = value.trim() ? { display: value } : undefined;
        return;
    }
    if (value?.id) {
        med.requester = { id: value.id, resourceType: 'Practitioner', display: value.fullName };
        return;
    }
    med.requester = undefined;
};

// History state
const historyData = ref(null);
const historyLoading = ref(false);

const fetchMedicationHistory = async (medicationId) => {
    if (!medicationId) {
        historyData.value = null;
        return;
    }
    historyLoading.value = true;
    try {
        const response = await $httpFhirApi.get(`MedicationRequest/${medicationId}/_history`);
        historyData.value = response.data?.entry?.map((e) => e.resource) || (Array.isArray(response.data) ? response.data : []);
    } catch (err) {
        console.error('Failed to fetch medication history:', err);
        historyData.value = [];
    } finally {
        historyLoading.value = false;
    }
};

const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, key) => {
        if (!o) return undefined;
        const match = key.match(/^(.+)\[(\d+)\]$/);
        if (match) return o[match[1]]?.[parseInt(match[2])];
        return o[key];
    }, obj);
};

const describeHistoryChanges = (current, previous) => {
    const changes = [];
    const curJson = typeof current.toJSON === 'function' ? current.toJSON() : current;
    const prevJson = typeof previous.toJSON === 'function' ? previous.toJSON() : previous;

    const fields = [
        { path: 'medication.CodeableConcept.text', label: 'Medication name' },
        { path: 'status', label: 'Status' },
        { path: 'ncpdpStatus', label: 'Pharmacy status' },
        { path: 'effective.start', label: 'Start date', format: formatDate },
        { path: 'effective.end', label: 'End date', format: formatEndOf },
        { path: 'dosageInstruction[0].timing.code.text', label: 'Frequency' },
        { path: 'requester.display', label: 'Prescriber' },
        { path: 'reasonCode[0].text', label: 'Reason' },
        { path: 'isMedicationEffective.display', label: 'Effectiveness' },
        { path: 'takenAsDirected.display', label: 'Taken as directed' },
        { path: 'includeInTreatmentPlan', label: 'Treatment plan', boolean: true },
    ];

    for (const field of fields) {
        const curVal = getNestedValue(curJson, field.path);
        const prevVal = getNestedValue(prevJson, field.path);
        if (String(curVal ?? '') !== String(prevVal ?? '')) {
            if (field.boolean) {
                changes.push(curVal ? `Added to ${field.label.toLowerCase()}` : `Removed from ${field.label.toLowerCase()}`);
            } else {
                const displayCur = field.format ? field.format(curVal) : curVal;
                const displayPrev = field.format ? field.format(prevVal) : prevVal;
                if (prevVal && curVal) {
                    changes.push(`${field.label}: ${displayPrev} → ${displayCur}`);
                } else if (curVal) {
                    changes.push(`${field.label}: ${displayCur}`);
                } else {
                    changes.push(`${field.label} removed`);
                }
            }
        }
    }

    return changes;
};

const isWebhookVersion = (entry, originalIndex) => {
    if (!historyData.value || originalIndex >= historyData.value.length - 1) return false;
    const prev = historyData.value[originalIndex + 1];
    const curLogLen = entry.scriptSureWebhookLog?.length ?? 0;
    const prevLogLen = prev.scriptSureWebhookLog?.length ?? 0;
    return curLogLen > prevLogLen;
};

const filteredHistoryData = computed(() => {
    if (!historyData.value) return null;
    if (historyData.value.length <= 1) {
        return historyData.value.map((entry, i) => ({ entry, originalIndex: i }));
    }
    const result = [];
    for (let i = 0; i < historyData.value.length; i++) {
        const isFirstVersion = i === historyData.value.length - 1;
        if (isFirstVersion || describeHistoryChanges(historyData.value[i], historyData.value[i + 1]).length) {
            result.push({ entry: historyData.value[i], originalIndex: i });
        }
    }
    return result;
});

// The oldest version of the unbroken run ending at the current one is the version that set the status.
const statusChangeVersion = computed(() => {
    const status = originalMedication.value?.status;
    if (!historyData.value?.length || !status) return null;
    let found = null;
    for (const version of historyData.value) {
        if (version.status !== status) break;
        found = version;
    }
    return found;
});

// What the saved medication is right now, when that is anything but plainly active.
const editingStatus = computed(() => {
    const med = originalMedication.value;
    if (!med || isNewMedication.value) return null;

    if (isMedicationInactive(med)) {
        const endedOnItsOwn = !ENDED_STATUSES.includes(med.status);
        const stopsOn = administrationEnd(med);
        return {
            label: inactiveLabel(med),
            type: inactiveColor(med) === 'error' ? 'error' : 'info',
            at: endedOnItsOwn ? null : statusChangedAt(med) || statusChangeVersion.value?.meta?.lastUpdated,
            by: endedOnItsOwn ? null : (statusChangeVersion.value?.updatedBy ?? null),
            detail: endedOnItsOwn ? `Ended ${formatDate(stopsOn)}` : stopsOn && stopsOn > todayInServiceZone() ? `Stops ${formatDate(stopsOn)}` : null,
        };
    }

    const held = heldSegmentOf(med);
    if (held) {
        const until = segmentEndDay(held);
        return {
            label: 'On hold',
            type: 'warning',
            at: held.addedOn || null,
            by: held.addedBy || null,
            detail: [`From ${formatDate(segmentStartOf(held))}`, until ? `until ${formatDate(until)}` : null].filter(Boolean).join(' '),
        };
    }

    if (med.status && med.status !== 'active') {
        return {
            label: med.status.charAt(0).toUpperCase() + med.status.slice(1),
            type: 'info',
            at: med.statusChanged || statusChangeVersion.value?.meta?.lastUpdated,
            by: statusChangeVersion.value?.updatedBy ?? null,
            detail: null,
        };
    }

    return null;
});

// Get medication status color (same as card)
const getStatusColor = (status) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'completed':
            return 'primary';
        case 'stopped':
        case 'cancelled':
            return 'error';
        case 'on-hold':
            return 'warning';
        case 'draft':
            return 'disabled';
        default:
            return 'primary';
    }
};

const ncpdpStatusConfig = {
    Sent: { label: 'Sent to Pharmacy', color: 'blue' },
    Acknowledged: { label: 'Pharmacy Acknowledged', color: 'green' },
    Accepted: { label: 'Pharmacy Accepted', color: 'green' },
    Cancelled: { label: 'Cancelled', color: 'error' },
    'RxChange Response': { label: 'Change Requested', color: 'orange' },
    'RxRenewal Response': { label: 'Renewal Response', color: 'teal' },
};

const getNcpdpDisplay = (ncpdpStatus) => {
    if (!ncpdpStatus) return null;
    if (ncpdpStatusConfig[ncpdpStatus]) return ncpdpStatusConfig[ncpdpStatus];
    if (ncpdpStatus.startsWith('Error')) return { label: 'Pharmacy Error', color: 'error' };
    return { label: ncpdpStatus, color: 'grey' };
};

// The row's text lines as data, so an absent value never reaches the template and never leaves an
// empty item behind for a separator to attach to.
const medicationRowSegments = (med) => {
    const frequency = sigTextOf(med) || '';
    const prescriber = medicationPrescriberDisplay(med);
    const reason = med.reasonCode?.[0]?.text || '';

    const primary = [];
    if (prescriber) primary.push({ key: 'prescriber', label: 'by', text: prescriber });
    // The Meds card has no room for a date range nobody reads there; the form widget and the dialog carry it.
    if (!isCompact.value && med.effective?.start) {
        primary.push({ key: 'dates', text: `${formatDate(med.effective.start)} - ${med.effective.end ? formatDate(med.effective.end) : 'Current'}` });
    } else if (!isCompact.value && med.effective?.end && !statusChangedAt(med)) {
        // A medication added here carries no start date, and the day it stopped is why it stays listed.
        primary.push({ key: 'stoppedOn', dataCy: 'stoppedOn', text: `Discontinued ${formatDate(med.effective.end)}` });
    }

    const secondary = [];
    if (reason) secondary.push({ key: 'reason', label: 'Reason', text: reason });
    if (med.takenAsDirected?.display) secondary.push({ key: 'takenAsDirected', label: 'Taken as directed', text: med.takenAsDirected.display });
    if (med.isMedicationEffective?.display) secondary.push({ key: 'effectiveness', label: 'Effectiveness', text: med.isMedicationEffective.display });

    // Every value a segment can truncate, so the name's tooltip can read each one back.
    const tooltip = [
        { key: 'frequency', label: 'Frequency', text: frequency },
        { key: 'prescriber', label: 'Prescriber', text: prescriber },
        { key: 'reason', label: 'Reason', text: reason },
    ].filter((line) => line.text);

    const chips = medicationRowChips(med);
    return { frequency, primary, secondary, tooltip, chips, hasChips: Object.values(chips).some(Boolean) };
};

// Each row's layout, built once and cached until that row's data changes rather than on every render.
const medicationRows = computed(() => displayedMedications.value.map((med) => ({ med, layout: medicationRowSegments(med) })));
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2">
        <div v-if="!isCompact" class="d-flex py-1 text-h2" style="color: #43a047">
            <FontAwesomeIcon size="lg" :icon="['far', 'pills']" class="align-self-center mr-2"></FontAwesomeIcon>
            <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
        </div>
        <div v-if="!canViewMedications" :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px; color: black' : 'color: black'">
            <div :class="{ inactiveFontSize: isLocked || printView }">
                <div class="text-wrap text-body-1">Permission denied</div>
            </div>
        </div>

        <DataProvider v-else ref="medicationsDP" v-model="medicationRequestData" collection autosave refresh-on-change v-bind="medicationQuery">
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn variant="flat" color="primary" @click="refresh">Try again</v-btn>
                </div>
            </template>
            <template #default>
                <div data-cy="currentMedicationList" data-linkid="medicationList">
                    <div v-if="!isCompact" class="text-medium-emphasis mb-2">Include current and any relevant past medications.</div>

                    <!-- Read-only medications list -->
                    <div :style="!isCompact ? 'border-left: 3px solid #43a047; opacity: 1; padding-left: 8px' : ''">
                        <template v-if="displayedMedications.length">
                            <!-- Reconcile mode toolbar -->
                            <!-- Reconcile mode: the row wraps at narrow widths so default-size buttons never clip; the global
                                 .v-btn + .v-btn rule spaces the buttons. Reconcile selected is the primary action. -->
                            <div v-if="reconcileMode" class="d-flex flex-wrap align-center px-2 pt-2" data-cy="reconcileToolbar">
                                <v-btn variant="text" color="primary" data-cy="toggleAllReconcile" @click="toggleAllForReconcile">
                                    {{ allSelectedForReconcile ? 'Deselect all' : 'Select all' }}
                                </v-btn>
                                <v-btn color="primary" variant="flat" :disabled="selectedCount === 0" :loading="reconciling" data-cy="reconcileSelectedBtn" @click="reconcileMedications">
                                    <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-2" />
                                    Reconcile selected ({{ selectedCount }})
                                </v-btn>
                                <v-spacer v-if="isCompact" />
                                <v-btn size="x-small" variant="text" color="secondary" data-cy="cancelReconcile" icon @click="toggleReconcileMode">
                                    <FontAwesomeIcon :icon="['far', 'times']" />
                                </v-btn>
                            </div>
                            <div v-if="isCompact && canReconcile && !reconcileMode && !isPortal" class="d-flex align-center px-2 pt-2">
                                <!-- Refresh lives on the card's own menu; a second ellipsis holding one item
                                     next to this button was a menu nobody would think to open. -->
                                <v-btn color="primary" variant="tonal" class="flex-grow-1" data-cy="reconcileMedicationsBtn" @click="toggleReconcileMode">
                                    <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-2" />
                                    Reconcile medications
                                </v-btn>
                            </div>

                            <v-list lines="two" class="pa-0 bg-transparent">
                                <template v-for="({ med, layout }, index) in medicationRows" :key="med.id || index">
                                    <v-divider v-if="index"></v-divider>
                                    <v-list-item
                                        :data-cy="'medicationItem-' + index"
                                        :class="{ 'cursor-pointer': !reconcileMode && !readOnly }"
                                        @click="reconcileMode ? toggleMedicationSelection(med.id) : openEditDialog(med)"
                                    >
                                        <template #prepend>
                                            <div class="reconcile-checkbox-wrapper" :class="{ 'reconcile-checkbox-active': reconcileMode }">
                                                <v-checkbox-btn
                                                    v-if="reconcileMode"
                                                    :model-value="selectedForReconcile.has(med.id)"
                                                    :data-cy="'reconcileCheckbox-' + index"
                                                    @update:model-value="toggleMedicationSelection(med.id)"
                                                    @click.stop
                                                />
                                            </div>
                                        </template>

                                        <!-- Every item draws the dot before it, and the first item on each wrapped line has its dot
                                             clipped (see .med-row-line), so a dot only ever sits between two values on one line. -->
                                        <div class="med-row-line text-body-1">
                                            <div class="med-row-items">
                                                <!-- Name and frequency are one item, joined by a space, never a dot. The name truncates
                                                     like the other patient-page cards; the tooltip reads back what truncation cuts. -->
                                                <div class="med-row-item med-row-headline font-weight-bold">
                                                    <v-tooltip open-delay="500" location="right">
                                                        <template #activator="{ props: tooltipProps }">
                                                            <span v-bind="tooltipProps" class="text-truncate" data-cy="medicationName">{{ med.medication?.CodeableConcept?.text }}</span>
                                                        </template>
                                                        <div class="px-2">{{ med.medication?.CodeableConcept?.text }}</div>
                                                        <div v-for="line in layout.tooltip" :key="line.key" class="px-2">{{ line.label }}: {{ line.text }}</div>
                                                    </v-tooltip>
                                                    <span v-if="layout.frequency" class="text-truncate" data-cy="medicationFrequency">{{ layout.frequency }}</span>
                                                </div>
                                                <span v-for="segment in layout.primary" :key="segment.key" class="med-row-item" :data-cy="segment.dataCy || 'medicationRowItem-' + segment.key">
                                                    <span class="text-truncate"
                                                        ><template v-if="segment.label">{{ segment.label }}&nbsp;</template>{{ segment.text }}</span
                                                    >
                                                </span>
                                                <!-- Each chip is its own item so dots separate them too. BnChip clips its overflow, which
                                                     would hide a dot drawn on the chip, so a span carries it. -->
                                                <div v-if="layout.hasChips" class="med-row-chips" data-cy="medicationChips">
                                                    <span v-if="layout.chips.inactive" class="med-row-item">
                                                        <BnChip native size="small" :color="inactiveColor(med)" data-cy="inactiveChip">
                                                            {{ inactiveLabel(med) }}<template v-if="statusChangedAt(med)">&nbsp;{{ formatMoment(statusChangedAt(med)) }}</template>
                                                        </BnChip>
                                                    </span>
                                                    <!-- Active goes unsaid: it is never absent and never informative. Draft, on-hold and unknown still state themselves. -->
                                                    <span v-else-if="layout.chips.status" class="med-row-item">
                                                        <BnChip native size="small" :color="getStatusColor(med.status)">
                                                            {{ med.status.charAt(0).toUpperCase() + med.status.slice(1) }}
                                                        </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.hold" class="med-row-item">
                                                        <BnChip native size="small" color="warning" data-cy="medicationHoldChip"> On hold </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.taper" class="med-row-item">
                                                        <BnChip native size="small" color="primary" data-cy="medicationTaperChip"> Taper </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.prn" class="med-row-item">
                                                        <BnChip native size="small" :color="PRN_COLOR" data-cy="medicationPrnChip"> PRN </BnChip>
                                                    </span>
                                                    <!-- A finished taper reads exactly like a running one without this. -->
                                                    <span v-if="layout.chips.scheduleComplete" class="med-row-item">
                                                        <BnChip native size="small" color="disabled" data-cy="medicationScheduleCompleteChip"> Schedule complete </BnChip>
                                                    </span>
                                                    <!-- An unscheduled medication produces no doses and appears in no pass, and administrationTypeOf
                                                         answers 'scheduled' off the order even with an empty chain. The card is where staff catch this. -->
                                                    <span v-if="layout.chips.needsSchedule" class="med-row-item">
                                                        <BnChip native size="small" color="warning" data-cy="medicationNeedsScheduleChip"> Not scheduled </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.needsReview" class="med-row-item">
                                                        <BnChip native size="small" color="warning" data-cy="medicationNeedsReviewChip"> Needs review </BnChip>
                                                    </span>
                                                    <!-- The clinic gives no doses in this window; the row's schedule alone does not say so. -->
                                                    <span v-if="layout.chips.selfAdmin" class="med-row-item">
                                                        <BnChip native size="small" color="info" data-cy="medicationSelfAdminChip"> Self-doses </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.inactiveReason" class="med-row-item">
                                                        <BnChip native size="small" color="disabled" class="text-truncate" style="max-width: 300px" data-cy="inactiveReasonChip">
                                                            Discontinue reason {{ med.statusReason.text }}
                                                        </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.prescribed" class="med-row-item">
                                                        <v-tooltip location="bottom">
                                                            <template #activator="{ props: tipProps }">
                                                                <BnChip v-bind="tipProps" native size="small" :color="ePrescribeChipFor(med).color" data-cy="prescribedChip">
                                                                    e-Rx
                                                                    <FontAwesomeIcon :icon="['far', ePrescribeChipFor(med).icon]" class="ml-2" />
                                                                </BnChip>
                                                            </template>
                                                            <div v-for="(entry, i) in ePrescribeChipFor(med).history" :key="i" class="text-caption" :class="{ 'font-weight-bold': i === 0 }">
                                                                {{ entry.event }}<template v-if="entry.receivedAt"> &mdash; {{ formatDate(entry.receivedAt) }}</template>
                                                            </div>
                                                            <div v-if="!ePrescribeChipFor(med).history.length" class="text-caption">{{ ePrescribeChipFor(med).label }}</div>
                                                        </v-tooltip>
                                                    </span>
                                                    <!-- Treatment plan indicator — hidden in treatment-plan-only contexts and in the portal -->
                                                    <span v-if="layout.chips.treatmentPlan" class="med-row-item">
                                                        <BnChip native size="small" color="tertiary" data-cy="treatmentPlanChip"> Tx plan </BnChip>
                                                    </span>
                                                    <span v-if="layout.chips.freeTextSync" class="med-row-item">
                                                        <v-tooltip location="top">
                                                            <template #activator="{ props: tipProps }">
                                                                <BnChip v-bind="tipProps" native size="small" color="warning" data-cy="freeTextSyncChip">
                                                                    <FontAwesomeIcon :icon="['far', 'exclamation-triangle']" class="mr-1" />
                                                                    Non e-Rx medication
                                                                </BnChip>
                                                            </template>
                                                            <span>Free-text medication — select from search results to enable e-Prescribe sync</span>
                                                        </v-tooltip>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="layout.secondary.length" class="med-row-line text-body-2 text-medium-emphasis">
                                            <div class="med-row-items">
                                                <span v-for="segment in layout.secondary" :key="segment.key" class="med-row-item" :data-cy="'medicationRowItem-' + segment.key">
                                                    <span class="text-truncate">{{ segment.label }}&nbsp;{{ segment.text }}</span>
                                                </span>
                                            </div>
                                        </div>
                                        <div v-if="getLastReconciliation(med)">
                                            <span class="text-caption text-grey" data-cy="reconcileInfo">
                                                <FontAwesomeIcon :icon="['far', 'check-double']" class="mr-1" />
                                                Reconciled {{ formatDate(getLastReconciliation(med).date) }} &mdash; {{ getDisplay(getLastReconciliation(med).practitioner) }}
                                            </span>
                                        </div>
                                    </v-list-item>
                                </template>
                            </v-list>
                        </template>

                        <!-- Treatment plan only - no medications flagged -->
                        <template v-else-if="treatmentPlanMedsOnly">
                            <v-list class="pa-0 bg-transparent">
                                <v-list-item class="d-flex justify-center align-center" data-cy="noTreatmentPlanMeds" title="No medications included in the treatment plan." />
                            </v-list>
                        </template>

                        <!-- Empty state for compact mode - hint to add -->
                        <template v-else-if="isCompact">
                            <v-card :border="false" flat class="d-flex text-caption pa-2 flex-grow-1">
                                <div class="ml-auto">
                                    Add medication
                                    <FontAwesomeIcon :icon="['far', 'level-up']" class="ml-2 mr-16" />
                                </div>
                            </v-card>
                        </template>
                    </div>

                    <!-- Action buttons -->
                    <div v-if="!isCompact && !viewOnlyMedications && !readOnly" class="d-flex flex-wrap ga-2 mt-2">
                        <v-btn color="primary" variant="tonal" class="v-list-item-pointer-hover" data-cy="addAnotherMedicationBtn" @click="openAddDialog">
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                            </template>
                            Add medication
                        </v-btn>
                        <v-btn
                            v-if="canReconcile && !reconcileMode && !isPortal"
                            color="primary"
                            variant="tonal"
                            class="v-list-item-pointer-hover"
                            data-cy="reconcileMedicationsBtn"
                            @click="toggleReconcileMode"
                        >
                            <template #prepend>
                                <FontAwesomeIcon :icon="['far', 'check-double']" size="sm" />
                            </template>
                            Reconcile medications
                        </v-btn>
                        <v-btn
                            v-if="canPrescribe"
                            variant="flat"
                            color="primary"
                            class="v-list-item-pointer-hover"
                            :loading="isPrescribing"
                            :disabled="isPrescribing"
                            data-cy="ePrescribeBtn"
                            @click="openPrescribe"
                        >
                            e-Prescribe
                            <FontAwesomeIcon :icon="['far', 'arrow-up-right-from-square']" class="ml-2" />
                        </v-btn>
                        <BnMenu icon="ellipsis-v" btn-size="x-small" data-cy="medicationsOverflowMenu">
                            <BnMenuItem
                                :icon="isRefreshing ? 'spinner' : 'sync'"
                                label="Refresh medications"
                                :disabled="isRefreshing"
                                data-cy="refreshMedicationsMenuItem"
                                @click="refreshMedications"
                            />
                            <BnMenuItem
                                :icon="showInactiveMedications ? 'eye-slash' : 'eye'"
                                :label="showInactiveMedications ? 'Hide inactive medications' : 'Show inactive medications'"
                                data-cy="toggleInactiveMedications"
                                @click="toggleInactiveMedications"
                            />
                        </BnMenu>
                    </div>
                </div>
            </template>
        </DataProvider>

        <!-- Confirmation Dialog -->
        <BnDialog ref="bnDialogRef" />

        <!-- Add/Edit Medication Dialog -->
        <v-dialog v-model="editDialog" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
            <v-card v-if="editingMedication" class="d-flex flex-column">
                <ion-header>
                    <ion-toolbar>
                        <v-toolbar density="compact" flat class="flex-grow-0">
                            <v-toolbar-title>{{ isNewMedication ? 'Add' : 'Edit' }} medication</v-toolbar-title>
                            <BnToolbarBtn type="secondary" label="Cancel" icon="times" data-cy="cancelMedicationEdit" @click="cancelEdit" />
                            <BnToolbarBtn v-if="!viewOnlyMedications" type="primary" right label="Save" icon="check" :disabled="!canSaveMedication" data-cy="saveMedication" @click="saveMedication" />
                            <BnMenu v-if="!isNewMedication" offset-y left icon="ellipsis-v" data-cy="medicationEditMenu">
                                <BnMenuItem v-if="!viewOnlyMedications" label="Save" icon="check" :disabled="!canSaveMedication" @click="saveMedication" />
                                <BnMenuItem label="Cancel" icon="times" @click="cancelEdit" />
                                <BnMenuItem
                                    v-if="!treatmentPlanMedsOnly && !isPortal && !viewOnlyMedications"
                                    :icon="editingMedication.includeInTreatmentPlan ? 'square-check' : 'square'"
                                    label="Include in treatment plan"
                                    data-cy="toggleTreatmentPlanBtn"
                                    @click="toggleTreatmentPlanFromDialog"
                                />
                                <BnMenuItem
                                    v-if="isInCompletionGrace(originalMedication) && !viewOnlyMedications"
                                    icon="check"
                                    label="Mark completed"
                                    data-cy="markCompletedBtn"
                                    @click="markCompleted(editingMedication)"
                                />
                                <BnMenuItem
                                    v-if="isEditingMedicationInactive && !viewOnlyMedications"
                                    icon="inbox-out"
                                    label="Restore medication"
                                    data-cy="restoreMedicationBtn"
                                    @click="restoreMedication(editingMedication)"
                                />
                                <template v-else-if="!viewOnlyMedications">
                                    <BnMenuItem v-if="!isPortal" icon="archive" label="Discontinue medication" data-cy="inactivateMedicationBtn" @click="inactivateMedication(editingMedication)" />
                                    <BnMenuItem icon="exclamation-triangle" label="Entered in error" data-cy="enteredInErrorBtn" @click="markEnteredInError(editingMedication)" />
                                </template>
                            </BnMenu>
                        </v-toolbar>
                    </ion-toolbar>
                </ion-header>
                <v-divider></v-divider>
                <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                    <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mb-4" data-cy="medicationSaveError">{{ saveError }}</v-alert>

                    <v-alert v-if="editingStatus" :type="editingStatus.type" variant="tonal" density="compact" class="mb-4" data-cy="medicationStatusBanner">
                        <span class="font-weight-bold" data-cy="medicationStatusLabel">{{ editingStatus.label }}</span>
                        <span v-if="editingStatus.at" data-cy="medicationStatusAt"> &middot; {{ formatMoment(editingStatus.at) }}</span>
                        <span v-if="editingStatus.by && getDisplay(editingStatus.by)" data-cy="medicationStatusBy"> &middot; by {{ getDisplay(editingStatus.by) }}</span>
                        <span v-if="editingStatus.detail" data-cy="medicationStatusDetail"> &middot; {{ editingStatus.detail }}</span>
                    </v-alert>

                    <!-- Naming who can change it is the point: "you cannot do this" without that
                         leaves someone guessing which colleague to find. -->
                    <v-alert v-if="viewOnlyMedications" type="info" variant="tonal" density="compact" class="mb-4" data-cy="viewOnlyMedicationAlert">
                        You can view this medication and its schedule. Changing it needs the medication list update permission - ask a prescriber or your administrator.
                    </v-alert>

                    <div class="d-flex flex-column">
                        <!-- Medication name -->
                        <div class="d-flex align-start">
                            <v-combobox
                                v-model:search="searchRxNorm"
                                :model-value="editingMedication.medication?.CodeableConcept?.text"
                                :items="medList"
                                :data-options="JSON.stringify(medList)"
                                :menu-props="{ closeOnContentClick: true }"
                                :disabled="!canEditOrderIdentity"
                                item-title="display"
                                return-object
                                label="Medication name"
                                data-cy="medicationField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'medicationName', true)"
                                @update:model-value="(item) => setMedication(editingMedication, item)"
                                @blur="onMedNameBlur"
                            >
                                <template v-if="showFreeTextWarning" #append>
                                    <v-tooltip location="bottom">
                                        <template #activator="{ props: tipProps }">
                                            <FontAwesomeIcon v-bind="tipProps" :icon="['far', 'exclamation-triangle']" class="text-warning" data-cy="freeTextMedicationWarning" />
                                        </template>
                                        <span>This medication was entered manually and won't sync to e-Prescribe. Select a medication from the search results to enable syncing.</span>
                                    </v-tooltip>
                                </template>
                                <template #item="{ item, props: itemProps }">
                                    <v-list-item v-bind="itemProps" :title="null">
                                        <v-list-item-title data-cy="medicationSearchItem">{{ item.title }}</v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                            <!-- Centred on the field's input row, not on the field: the details area
                                 below it is what a validation message expands into, and the chip
                                 should not move when one appears. -->
                            <div v-if="isTypeLocked && !needsSchedule && !needsReview" class="d-flex align-center ml-2" style="height: 40px">
                                <BnChip native size="small" variant="tonal" :color="administrationTypeColor(administrationType)" data-cy="administrationTypeDisplay">
                                    {{ administrationTypeLabel }}
                                </BnChip>
                            </div>
                            <!-- Where the prescription stands reads beside the drug rather than as a
                                 banner above the form; the pharmacy's own words are the hover. -->
                            <div v-if="editingMedIsPrescribed" class="d-flex align-center ml-2" style="height: 40px">
                                <v-tooltip location="bottom">
                                    <template #activator="{ props: tipProps }">
                                        <BnChip v-bind="tipProps" native size="small" variant="tonal" :color="ePrescribeChip.color" data-cy="ePrescribeChip">
                                            e-Rx
                                            <FontAwesomeIcon :icon="['far', ePrescribeChip.icon]" class="ml-2" />
                                        </BnChip>
                                    </template>
                                    <div v-for="(entry, i) in ePrescribeChip.history" :key="i" class="text-caption" :class="{ 'font-weight-bold': i === 0 }">
                                        {{ entry.event }}<template v-if="entry.receivedAt"> &mdash; {{ formatDate(entry.receivedAt) }}</template>
                                    </div>
                                    <div v-if="!ePrescribeChip.history.length" class="text-caption">{{ ePrescribeChip.label }}</div>
                                </v-tooltip>
                            </div>
                            <div v-if="needsSchedule" class="d-flex align-center ml-2" style="height: 40px">
                                <BnChip native size="small" variant="tonal" color="warning" data-cy="needsScheduleChip"> Not scheduled </BnChip>
                            </div>
                            <div v-if="needsReview" class="d-flex align-center ml-2" style="height: 40px">
                                <v-tooltip location="bottom" :text="needsReviewHint">
                                    <template #activator="{ props: tipProps }">
                                        <BnChip v-bind="tipProps" native size="small" variant="tonal" color="warning" data-cy="needsReviewChip"> Needs review </BnChip>
                                    </template>
                                </v-tooltip>
                            </div>
                        </div>

                        <!-- Prescriber — suggests active in-house providers, accepts free text for an outside prescriber -->
                        <v-combobox
                            v-model:menu="prescriberMenu"
                            :model-value="editingPrescriberDisplay"
                            :items="prescriberMatches"
                            no-filter
                            :disabled="editingMedIsPrescribed || !editingMedication.medication?.CodeableConcept?.text"
                            item-title="fullName"
                            return-object
                            label="Prescriber"
                            data-cy="prescriberField"
                            @update:focused="onPrescriberFocused"
                            @update:search="onPrescriberSearch"
                            @update:model-value="(value) => setPrescriber(value, editingMedication)"
                        >
                            <template #item="{ item, props: itemProps }">
                                <v-list-item v-bind="itemProps" :title="null">
                                    <v-list-item-title data-cy="prescriberSearchItem">{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </template>
                        </v-combobox>

                        <!-- Only once somebody has discontinued it. An end date alone is not that: a
                             prescription stating "for 5 days" arrives with one on day one. -->
                        <v-text-field
                            v-if="DISCONTINUED_STATUSES.includes(editingMedication.status) || editingMedication.statusReason?.text"
                            :model-value="editingMedication.statusReason?.text"
                            :disabled="editingMedIsPrescribed || viewOnlyMedications"
                            label="Discontinue reason"
                            data-cy="inactiveReasonField"
                            @update:model-value="(val) => (editingMedication.statusReason = val ? { text: val } : undefined)"
                        ></v-text-field>

                        <!-- Dosage: what the order says to give, and how often. -->
                        <div class="d-flex flex-wrap">
                            <v-combobox
                                :model-value="editingDose?.value"
                                :items="doseAmountOptions"
                                :disabled="!canEditDose"
                                label="Dose"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 110px"
                                data-cy="doseAmountField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'dose', true)"
                                @update:model-value="(val) => setDoseQuantity({ value: val })"
                            ></v-combobox>
                            <v-combobox
                                :model-value="editingDose?.unit"
                                :items="doseUnitOptions"
                                :disabled="!canEditDose"
                                label="Unit"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 140px"
                                data-cy="doseUnitSelect"
                                @update:model-value="(val) => setDoseQuantity({ unit: val })"
                            ></v-combobox>
                            <v-combobox
                                :model-value="editingCarrier?.route?.text"
                                :items="routeOptions"
                                :disabled="!canEditRoute"
                                label="Route"
                                class="flex-grow-1"
                                style="min-width: 150px"
                                data-cy="routeSelect"
                                @update:model-value="setRoute"
                            ></v-combobox>
                        </div>

                        <!-- The type decides what the rest of the dialog asks for, so it is asked before any of
                             that: after the drug and its dose, which every type shares, and ahead of the
                             frequency, dates and limits it chooses between. Once saved it is settled, and
                             reads as a chip beside the drug it describes. -->
                        <v-radio-group
                            v-if="!isTypeLocked && !isPortal"
                            :model-value="administrationType"
                            :disabled="!canEditSchedule"
                            inline
                            hide-details
                            class="mb-4"
                            data-cy="administrationTypeChooser"
                            @update:model-value="onAdministrationTypeChosen"
                        >
                            <v-radio
                                v-for="option in offeredAdministrationTypes"
                                :key="option.value"
                                :label="option.title"
                                :value="option.value"
                                class="mr-6"
                                :data-cy="`administrationType-${option.value}`"
                            ></v-radio>
                        </v-radio-group>
                        <!-- A patient does not classify their medication. As needed is what they can tell
                             us, and it is recorded as what they said - not as a PRN order, whose limits and
                             type are the clinic's to set at reconciliation. -->
                        <v-checkbox
                            v-if="isPortal && !isTypeLocked"
                            :model-value="reportedAsNeeded"
                            :disabled="!canEditDose"
                            label="Taken as needed (PRN)"
                            color="primary"
                            density="compact"
                            hide-details
                            class="mb-4"
                            data-cy="portalAsNeededCheckbox"
                            @update:model-value="(value) => setReportedAsNeeded(value === true)"
                        ></v-checkbox>

                        <!-- A taper is a chain of ordinary schedules, so it takes the same order.
                             Its steps vary the dose and the times; the interval they repeat on is this one.
                             Unknown states neither a frequency nor PRN limits. -->
                        <div v-if="administrationType === 'scheduled' || administrationType === 'taper'" class="d-flex flex-wrap align-center">
                            <v-combobox
                                :model-value="editingOrderTiming?.frequency"
                                :items="orderFrequencyOptions"
                                :disabled="!canEditDose"
                                label="Frequency"
                                suffix="times"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 170px"
                                data-cy="orderFrequencyField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'frequency', true)"
                                @update:model-value="(val) => setOrderTiming({ frequency: val })"
                            ></v-combobox>
                            <span class="text-caption text-medium-emphasis text-no-wrap flex-shrink-0 mr-2 mb-5">every</span>
                            <v-combobox
                                :model-value="editingOrderTiming?.period"
                                :items="orderIntervalOptions"
                                :disabled="!canEditDose"
                                label="Interval"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 130px"
                                data-cy="orderIntervalField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'interval', true)"
                                @update:model-value="(val) => setOrderTiming({ period: val })"
                            ></v-combobox>
                            <v-select
                                :model-value="editingOrderTiming?.periodUnit || 'd'"
                                :items="INTERVAL_UNIT_OPTIONS"
                                :disabled="!canEditDose"
                                label="Unit"
                                class="flex-grow-0 flex-shrink-0"
                                style="width: 120px"
                                data-cy="orderIntervalUnitSelect"
                                @update:model-value="(val) => setOrderTiming({ periodUnit: val })"
                            ></v-select>
                        </div>

                        <div v-if="administrationType === 'prn'" class="d-flex flex-wrap align-center">
                            <v-combobox
                                :model-value="editingCarrier?.maxPerDay"
                                :items="prnMaxPerDayOptions"
                                :disabled="!canEditDose"
                                label="Maximum per day"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 185px"
                                data-cy="orderMaxPerDayField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'maxPerDay', true)"
                                @update:model-value="(val) => setOrderPrn({ maxPerDay: val })"
                            ></v-combobox>
                            <v-combobox
                                :model-value="editingCarrier?.minimumInterval?.value"
                                :items="prnMinimumIntervalOptions"
                                :disabled="!canEditDose"
                                label="Minimum interval"
                                class="mr-2 flex-grow-0 flex-shrink-0"
                                style="width: 190px"
                                data-cy="orderMinimumIntervalField"
                                v-bind="$bnVuelidateErrorExtractor(v$.medicationForm, 'minimumInterval', true)"
                                @update:model-value="(val) => setOrderPrn({ minimumIntervalValue: val })"
                            ></v-combobox>
                            <v-select
                                :model-value="editingCarrier?.minimumInterval?.code || 'h'"
                                :items="MINIMUM_INTERVAL_UNIT_OPTIONS"
                                :disabled="!canEditDose"
                                label="Unit"
                                class="flex-grow-0 flex-shrink-0"
                                style="width: 120px"
                                data-cy="orderMinimumIntervalUnitSelect"
                                @update:model-value="(val) => setOrderPrn({ minimumIntervalUnit: val })"
                            ></v-select>
                        </div>

                        <!-- The sig reads back what the fields above compose, so it follows them: dose, then
                             how often or the as-needed limits. -->
                        <v-text-field
                            :model-value="editingMedIsPrescribed ? prescribedSig : generatedSig"
                            disabled
                            :label="editingMedIsPrescribed ? 'Sig' : 'Generated sig'"
                            data-cy="frequencyField"
                        ></v-text-field>

                        <!-- A PRN order's only window carries its own start and end below, so it states no
                             second pair here. -->
                        <div v-if="administrationType !== 'prn'" class="d-flex flex-wrap align-center">
                            <div class="flex-shrink-0 mr-2" style="width: 170px">
                                <BnDatePicker
                                    :model-value="effectiveStart"
                                    :disabled="!canEditEffectiveDates"
                                    :strict-date="false"
                                    clearable
                                    label="Effective start"
                                    data-cy="effectiveStartDate"
                                    @update:model-value="(val) => updateEffective(editingMedication, 'start', val)"
                                ></BnDatePicker>
                            </div>
                            <div class="flex-shrink-0" style="width: 170px">
                                <BnDatePicker
                                    :model-value="effectiveEnd"
                                    :disabled="!canEditEffectiveDates"
                                    :strict-date="false"
                                    clearable
                                    label="Effective end"
                                    :error-messages="effectiveEndError"
                                    data-cy="effectiveEndDate"
                                    @update:model-value="(val) => updateEffective(editingMedication, 'end', val)"
                                ></BnDatePicker>
                            </div>
                        </div>

                        <div v-if="administrationType === 'prn'" class="d-flex flex-wrap align-center">
                            <div class="flex-shrink-0 mr-2" style="width: 150px">
                                <BnDatePicker
                                    v-model="prnStart"
                                    :disabled="!canEditSchedule || isTypeLocked"
                                    :strict-date="false"
                                    :error-messages="prnStartError"
                                    label="Start date"
                                    data-cy="prnStartDate"
                                ></BnDatePicker>
                                <div v-if="prnStartsInPast" class="text-caption text-warning" data-cy="prnStartsInPastMessage">A schedule starts today or later.</div>
                            </div>
                            <div class="flex-shrink-0 mr-4" style="width: 150px">
                                <BnDatePicker
                                    v-model="prnEnd"
                                    :disabled="!canEditSchedule || isTypeLocked"
                                    :strict-date="false"
                                    :error-messages="prnEndError"
                                    label="End date"
                                    data-cy="prnEndDate"
                                ></BnDatePicker>
                            </div>
                            <!-- Only an as-needed order is self-administered at this level: it has no
                                 windows of its own to say it on, so an inhaler the patient carries is
                                 a fact about the order. A scheduled or tapering medication says it per
                                 window instead. Settled once the medication starts, like the dose. -->
                            <v-checkbox
                                v-if="emarEnabled"
                                :model-value="prnSelfAdministered"
                                :disabled="!canEditDose"
                                label="Self-doses"
                                color="primary"
                                density="compact"
                                hide-details
                                class="flex-grow-0 mb-5"
                                data-cy="prnSelfAdministered"
                                @update:model-value="(value) => (prnSelfAdministered = value === true)"
                            ></v-checkbox>
                        </div>

                        <div v-if="editingDoseFreeText" class="text-caption text-medium-emphasis mb-3" data-cy="doseFreeTextMessage">Recorded dose: {{ editingDoseFreeText }}</div>
                    </div>

                    <div v-if="isDoseLocked" class="text-body-2 text-medium-emphasis mb-2" data-cy="doseLockedMessage">
                        To change the dose or route, discontinue this medication and create a new order.
                    </div>

                    <!-- Schedule options — what the chosen type needs on top of the order. A new PRN
                         order has nothing to put here: its dates sit with the limits they bound, and its
                         segment is built from the order on save. Never in the portal: a schedule is what
                         the eMAR gives doses from, and a patient does not write one. Nor under Unknown,
                         which has no order for a schedule to give. -->
                    <BnListBox
                        v-if="!isPortal && emarEnabled && administrationType !== 'unknown' && (administrationType !== 'prn' || !isNewMedication)"
                        title="Administration schedule"
                        :info="administrationScheduleInfo"
                        data-cy="schedulingSection"
                    >
                        <v-card flat border="none" class="pb-1">
                            <div :data-cy="scheduleSectionCy">
                                <v-list v-if="editingSegments.length" density="compact" class="py-0" data-cy="segmentChainSummary">
                                    <template v-for="(segment, index) in editingSegments" :key="index">
                                        <v-divider v-if="index"></v-divider>
                                        <v-list-item class="px-2" :data-cy="`segmentRow${index}`" @click="canOpenSegment && openSegmentDialog(segment)">
                                            <template #prepend>
                                                <FontAwesomeIcon :icon="['far', 'pills']" class="mr-3 text-medium-emphasis" />
                                            </template>
                                            <v-list-item-title class="text-body-2">
                                                {{ describeSegment(segment) }}
                                                <BnChip
                                                    v-if="editingSegments.length > 1 && segment === currentSegment"
                                                    native
                                                    size="small"
                                                    color="success"
                                                    class="ml-2"
                                                    :data-cy="`segmentCurrentChip${index}`"
                                                >
                                                    Current
                                                </BnChip>
                                                <!-- The clinic expects no doses in this window, which the times alone do not say. -->
                                                <BnChip v-if="segment.selfAdministered === true" native size="small" color="info" class="ml-2" :data-cy="`segmentSelfAdminChip${index}`">
                                                    Self-doses
                                                </BnChip>
                                            </v-list-item-title>
                                            <v-list-item-subtitle class="text-body-2">
                                                Starts {{ formatDate(segmentStartOf(segment)) }}<span v-if="segmentSiteLabel(segment)"> · {{ segmentSiteLabel(segment) }}</span>
                                            </v-list-item-subtitle>
                                        </v-list-item>
                                    </template>
                                </v-list>

                                <v-divider v-if="editingSegments.length" class="mb-1"></v-divider>

                                <div class="d-flex flex-wrap">
                                    <v-btn
                                        v-if="administrationType === 'scheduled'"
                                        variant="text"
                                        color="primary"
                                        :disabled="!canEditSchedule"
                                        data-cy="changeScheduleBtn"
                                        @click="chooseAdministrationType(administrationType)"
                                    >
                                        <FontAwesomeIcon :icon="['far', 'pen']" class="mr-1" />
                                        {{ !isNewMedication && editingSegments.length ? 'Change schedule' : 'Set schedule' }}
                                    </v-btn>
                                    <v-btn v-else-if="administrationType === 'taper'" variant="text" color="primary" :disabled="!canEditSchedule" data-cy="addTaperBtn" @click="openTaperDialog">
                                        <FontAwesomeIcon :icon="['far', 'arrow-trend-down']" class="mr-1" />
                                        {{ editingSegments.length ? 'Add taper step' : 'Set taper' }}
                                    </v-btn>
                                    <v-btn v-if="canHold" variant="text" color="primary" :disabled="!canEditSchedule" data-cy="holdMedicationBtn" @click="openSegmentDialog(null, 'hold')">
                                        <FontAwesomeIcon :icon="['far', 'circle-pause']" class="mr-1" />
                                        Hold medication
                                    </v-btn>
                                    <v-btn v-else-if="heldSegment" variant="text" color="primary" :disabled="!canEditSchedule" data-cy="releaseHoldBtn" @click="releaseHold">
                                        <FontAwesomeIcon :icon="['far', 'play']" class="mr-1" />
                                        Release hold
                                    </v-btn>
                                </div>
                            </div>
                        </v-card>
                    </BnListBox>

                    <!-- The as-needed order's alone. A schedule states its own site on the window
                         it belongs to, because a schedule is what moves - a patient steps down to
                         another program and the next segment names the new place. A PRN has no
                         schedule to state it on, so the order carries the answer.

                         Only this patient's own places are offered: a flat list would offer an
                         office in another city just as readily. Never locked, unlike the dose and
                         the route; doses already made keep the site they were made at. -->
                    <v-select
                        v-if="emarEnabled && administrationSites.length && administrationType === 'prn'"
                        :model-value="editingMedication.administeringLocation?.id || null"
                        :items="administrationSites"
                        :disabled="viewOnlyMedications"
                        item-title="label"
                        item-value="id"
                        clearable
                        :label="prnSelfAdministered ? 'Dispense site' : 'Administration site'"
                        hide-details
                        class="mt-4 mb-2"
                        style="max-width: 320px"
                        data-cy="administrationSiteSelect"
                        @update:model-value="setAdministrationSite"
                    ></v-select>

                    <!-- The clinic's instructions to the nurse giving the dose; nothing a patient writes. -->
                    <v-textarea
                        v-if="!isPortal && emarEnabled"
                        :model-value="editingCarrier?.patientInstruction"
                        :disabled="!canEditSchedule"
                        label="Administration instructions"
                        rows="2"
                        auto-grow
                        data-cy="administrationInstructionsField"
                        @update:model-value="setAdministrationInstructions"
                    ></v-textarea>

                    <!-- Clinical assessment — how the medication is working, not how it is given -->
                    <BnListBox title="Clinical assessment" data-cy="clinicalAssessmentSection">
                        <v-card flat border="none" class="px-2 pt-2 pb-1">
                            <!-- Reason for taking - first, because adherence and effectiveness are judged against it -->
                            <v-text-field
                                :model-value="editingMedication.reasonCode?.[0]?.text"
                                :disabled="!editingMedication.medication?.CodeableConcept?.text"
                                label="Reason for taking medication"
                                data-cy="symptomField"
                                @update:model-value="(val) => (editingMedication.reasonCode = val ? [{ text: val }] : undefined)"
                            ></v-text-field>

                            <v-autocomplete
                                v-model="editingMedication.takenAsDirected"
                                :disabled="!editingMedication.medication?.CodeableConcept?.text"
                                :items="takenAsDirectedOptions"
                                :data-options="JSON.stringify(takenAsDirectedOptions)"
                                item-title="display"
                                item-value="code"
                                return-object
                                label="Taken as directed?"
                                class="flex-grow-1"
                                style="min-width: 150px"
                                clearable
                                data-cy="currentMedsTakenAsDirectedSelect"
                                @click:clear="editingMedication.takenAsDirected = undefined"
                            >
                                <template #no-data>
                                    <v-list-item>
                                        <v-list-item-title> No option found </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>

                            <!-- Effectiveness -->
                            <v-autocomplete
                                v-model="editingMedication.isMedicationEffective"
                                :disabled="!editingMedication.medication?.CodeableConcept?.text"
                                :items="effectivenessOptions"
                                :data-options="JSON.stringify(effectivenessOptions)"
                                item-title="display"
                                item-value="code"
                                return-object
                                label="Effectiveness"
                                clearable
                                data-cy="currentMedsIsEffectiveSelect"
                                @click:clear="editingMedication.isMedicationEffective = undefined"
                            >
                                <template #no-data>
                                    <v-list-item>
                                        <v-list-item-title> No option found </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>

                            <!-- "Include in treatment plan" checkbox — also toggleable via the ellipsis menu item above -->
                            <v-checkbox
                                v-if="!treatmentPlanMedsOnly && !isPortal"
                                v-model="editingMedication.includeInTreatmentPlan"
                                :disabled="!editingMedication.medication?.CodeableConcept?.text"
                                label="Include in treatment plan"
                                density="comfortable"
                                hide-details
                                data-cy="includeInTreatmentPlanCheckbox"
                            />
                        </v-card>
                    </BnListBox>

                    <!-- History -->
                    <template v-if="!isNewMedication">
                        <BnListBox title="History" data-cy="medicationHistory">
                            <v-card v-if="historyLoading" flat border="none" class="d-flex text-caption mb-2 pt-2 pr-2 flex-grow-1">
                                <div class="ml-2">
                                    <FontAwesomeIcon :icon="['far', 'spinner']" class="fa-spin mr-1" />
                                    Loading...
                                </div>
                            </v-card>
                            <v-card v-else-if="!filteredHistoryData || !filteredHistoryData.length" flat border="none" class="d-flex text-caption mb-2 pt-2 pr-2 flex-grow-1">
                                <div class="ml-2">No history found</div>
                            </v-card>
                            <v-card v-else flat border="none" style="max-height: 300px; overflow-y: auto">
                                <v-list>
                                    <template v-for="(h, index) in filteredHistoryData" :key="index">
                                        <v-divider v-if="index" class="my-2" />
                                        <v-row class="v-row--no-gutters">
                                            <v-col cols="7" class="align-self-center pl-4">
                                                <BnChip native :color="getStatusColor(h.entry.status)">
                                                    {{ h.entry.status?.charAt(0).toUpperCase() + h.entry.status?.slice(1) }}
                                                </BnChip>
                                                <span v-if="h.originalIndex === historyData.length - 1" class="pl-1">
                                                    <BnChip native color="success">Record created</BnChip>
                                                </span>
                                                <div v-else class="mt-1">
                                                    <div
                                                        v-for="(change, cIdx) in describeHistoryChanges(h.entry, historyData[h.originalIndex + 1])"
                                                        :key="cIdx"
                                                        class="text-caption text-medium-emphasis"
                                                    >
                                                        {{ change }}
                                                    </div>
                                                </div>
                                            </v-col>
                                            <v-col cols="5" class="text-caption text-right pr-4">
                                                <span :class="{ 'text-grey': h.originalIndex === historyData.length - 1 }">
                                                    Updated by {{ isWebhookVersion(h.entry, h.originalIndex) ? 'e-Prescribe' : getDisplay(h.entry.updatedBy) || '—' }}
                                                    <br />
                                                    {{ formatMoment(h.entry.meta?.lastUpdated) }}
                                                </span>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-list>
                            </v-card>
                        </BnListBox>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Segment Dialog -->
        <BnMedicationSegmentDialog
            v-if="editingMedication && !isPortal && emarEnabled"
            v-model="segmentDialog"
            :initial-type="initialSegmentType"
            :template-segment="segmentTemplate"
            :sig="editingMedIsPrescribed ? prescribedSig : generatedSig"
            :segment-saved="isSegmentSaved(editingSegment)"
            :order-frequency="orderFrequency"
            :order-period="orderPeriod"
            :order-period-unit="orderPeriodUnit"
            :medication="editingMedication"
            :segment="editingSegment"
            :sites="administrationSites"
            :time-zone="scheduleTimeZone"
            :read-only="viewOnlyMedications"
            @saved="onSegmentSaved"
            @deleted="onSegmentDeleted"
        />

        <!-- Taper Dialog -->
        <BnMedicationTaperDialog
            v-if="editingMedication && !isPortal && emarEnabled"
            v-model="taperDialog"
            :medication="editingMedication"
            :saved="!isNewMedication"
            :sig="editingMedIsPrescribed ? prescribedSig : generatedSig"
            :order-frequency="orderFrequency"
            :order-period="orderPeriod"
            :order-period-unit="orderPeriodUnit"
            :sites="administrationSites"
            :time-zone="scheduleTimeZone"
            :read-only="viewOnlyMedications"
            @saved="onTaperSaved"
        />

        <!-- Discontinue Medication Dialog -->
        <v-dialog v-model="discontinueDialog" persistent max-width="500px" data-cy="discontinueDialog">
            <v-card>
                <v-toolbar density="compact" flat>
                    <v-toolbar-title>Discontinue medication</v-toolbar-title>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <div style="max-width: 200px">
                        <BnDatePicker
                            :model-value="discontinueDate"
                            label="Discontinued date"
                            data-cy="discontinueDateField"
                            :strict-date="false"
                            :messages="discontinueStartsInPast ? ['Earliest is today. Discontinuing is immediate.'] : []"
                            @update:model-value="(val) => (discontinueDate = val)"
                        ></BnDatePicker>
                    </div>
                    <!-- A combobox, not a select: the list is a starting point, and it comes back
                         empty on a box the terminology migration has not reached, which must not
                         leave anyone unable to stop a medication. -->
                    <v-combobox v-model="discontinueReason" :items="discontinueReasonItems" :return-object="false" label="Reason" data-cy="discontinueReasonField" hide-details="auto"></v-combobox>

                    <!-- Fresh owns administration; it does not own the prescription. Saying so
                         here is the difference between a nurse knowing to call the pharmacy and
                         assuming this did it for her. -->
                    <v-alert v-if="discontinueNeedsPharmacyNotice" type="info" variant="tonal" density="compact" class="mt-3" data-cy="discontinuePharmacyNotice">
                        This stops the clinic giving the medication. It does not cancel the prescription at the pharmacy.
                    </v-alert>

                    <v-alert v-if="discontinueError" type="error" variant="tonal" density="compact" class="mt-3" data-cy="discontinueError">{{ discontinueError }}</v-alert>
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                    <v-spacer></v-spacer>
                    <v-btn variant="tonal" color="secondary" data-cy="cancelDiscontinueBtn" @click="cancelDiscontinue">Cancel</v-btn>
                    <v-btn variant="flat" color="primary" :disabled="!canConfirmDiscontinue || discontinuing" :loading="discontinuing" data-cy="confirmDiscontinueBtn" @click="confirmDiscontinue"
                        >Discontinue</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ScriptSure missing data notification -->
        <v-snackbar v-model="scriptSureSnackbar" :timeout="6000" color="warning" location="bottom" multi-line>
            {{ scriptSureSnackbarMessage }}
            <template #actions>
                <v-btn variant="text" color="white" @click="scriptSureSnackbar = false">Close</v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.reconcile-checkbox-wrapper {
    width: 0;
    opacity: 0;
    overflow: hidden;
    transition:
        width 0.2s ease,
        opacity 0.2s ease;
}

.reconcile-checkbox-active {
    width: 40px;
    opacity: 1;
}

/* Wrapped dot list: each item draws its dot in the margin before it, and the row is shifted left by
   that margin inside a clipping line, so the first item on every wrapped line has its dot cut off. */
.med-row-line {
    --med-sep: 1em;
    overflow: hidden;
}

.med-row-items {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 4px;
    margin-left: calc(-1 * var(--med-sep));
}

.med-row-item {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: calc(100% - var(--med-sep));
    margin-left: var(--med-sep);
}

/* Absolutely positioned so the dot sits in the margin and never counts as a flex child or gap. */
.med-row-item::before {
    content: '·';
    position: absolute;
    left: calc(-1 * var(--med-sep));
    width: var(--med-sep);
    text-align: center;
    font-weight: normal;
}

/* The chip group only gates rendering; its chips lay out as row items. */
.med-row-chips {
    display: contents;
}

.med-row-headline {
    flex-wrap: wrap;
    column-gap: 0.3em;
}

.med-row-headline > * {
    min-width: 0;
    max-width: 100%;
}
</style>
